'use client';

/* eslint-disable one-var */
/* eslint-disable vars-on-top */
/* eslint-disable no-var */
import * as React from 'react';
import {
  bin as d3Bin,
  extent as d3Extent,
  sum as d3Sum,
  min as d3Min,
  max as d3Max,
  range as d3Range,
  Bin,
} from 'd3-array';
import { scaleLinear as d3ScaleLinear } from 'd3-scale';
import { format as d3Format } from 'd3-format';
import { DonutChartProps } from '../DonutChart/index';
import {
  ChartDataPoint,
  ChartProps,
  HorizontalBarChartWithAxisDataPoint,
  LineChartPoints,
  VerticalStackedChartProps,
  HeatMapChartData,
  HeatMapChartDataPoint,
  VerticalBarChartDataPoint,
  SankeyChartData,
  LineChartLineOptions,
  GanttChartDataPoint,
  ScatterChartPoints,
} from '../../types/DataPoint';
import { SankeyChartProps } from '../SankeyChart/index';
import { VerticalStackedBarChartProps } from '../VerticalStackedBarChart/index';
import { HorizontalBarChartWithAxisProps } from '../HorizontalBarChartWithAxis/index';
import { LineChartProps } from '../LineChart/index';
import { AreaChartProps } from '../AreaChart/index';
import { HeatMapChartProps } from '../HeatMapChart/index';
import { DataVizPalette, getColorFromToken } from '../../utilities/colors';
import { GaugeChartProps, GaugeChartSegment } from '../GaugeChart/index';
import { GroupedVerticalBarChartProps } from '../GroupedVerticalBarChart/index';
import { VerticalBarChartProps } from '../VerticalBarChart/index';
import { ChartTableProps } from '../ChartTable/index';
import { GanttChartProps } from '../GanttChart/index';
import type { AnnotationOnlyChartProps } from '../AnnotationOnlyChart/AnnotationOnlyChart.types';
import {
  DEFAULT_DATE_STRING,
  findNumericMinMaxOfY,
  formatScientificLimitWidth,
  MIN_DONUT_RADIUS,
  calculatePrecision,
  precisionRound,
} from '../../utilities/utilities';
import type {
  Datum,
  Layout,
  PlotlySchema,
  PieData,
  PlotData,
  SankeyData,
  ScatterLine,
  TypedArray,
  Data,
  TableData,
  Color,
  LayoutAxis,
  XAxisName,
  TraceInfo,
  DTickValue,
  AxisType,
  Shape,
  Annotations,
  PolarLayout,
} from '@fluentui/chart-utilities';
import {
  isArrayOrTypedArray,
  isDate,
  isDateArray,
  isNumberArray,
  isStringArray,
  isYearArray,
  isInvalidValue,
  formatToLocaleString,
  isNumber,
  isObjectArray,
  getAxisIds,
  getAxisKey,
  isScatterAreaChart,
} from '@fluentui/chart-utilities';
import { curveCardinal as d3CurveCardinal } from 'd3-shape';
import type { ColorwayType } from './PlotlyColorAdapter';
import { getOpacity, extractColor, resolveColor, createColorScale } from './PlotlyColorAdapter';
import { rgb } from 'd3-color';
import { Legend, LegendsProps } from '../Legends/index';
import { ScatterChartProps } from '../ScatterChart/ScatterChart.types';
import { CartesianChartProps } from '../CommonComponents/index';
import { FunnelChartDataPoint, FunnelChartProps } from '../FunnelChart/FunnelChart.types';
import { PolarAxisProps, PolarChartProps } from '../PolarChart/PolarChart.types';
import {
  ChartAnnotation,
  ChartAnnotationArrowHead,
  ChartAnnotationLayoutProps,
  ChartAnnotationStyleProps,
  ChartAnnotationHorizontalAlign,
  ChartAnnotationVerticalAlign,
} from '../../types/ChartAnnotation';
import type { TitleStyles } from '../../utilities/Common.styles';

export const NON_PLOT_KEY_PREFIX = 'nonplot_';
export const SINGLE_REPEAT = 'repeat(1, 1fr)';

type DomainInterval = {
  start: number;
  end: number;
};

type ExtDomainInterval = DomainInterval & {
  cellName: string;
};

export type AxisProperties = {
  xAnnotation?: string;
  yAnnotation?: string;
  row: number;
  column: number;
  xDomain: DomainInterval;
  yDomain: DomainInterval;
};
export type GridAxisProperties = Record<string, AxisProperties>;

export type GridProperties = {
  templateRows: string;
  templateColumns: string;
  layout: GridAxisProperties;
};

type ScatterChartTypes = 'area' | 'line' | 'scatter';
interface SecondaryYAxisValues {
  secondaryYAxistitle?: string;
  secondaryYScaleOptions?: { yMinValue?: number; yMaxValue?: number };
}

const dashOptions = {
  dot: {
    strokeDasharray: '1, 5',
    strokeLinecap: 'round',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
  dash: {
    strokeDasharray: '5, 5',
    strokeLinecap: 'butt',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
  longdash: {
    strokeDasharray: '10, 5',
    strokeLinecap: 'butt',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
  dashdot: {
    strokeDasharray: '5, 5, 1, 5',
    strokeLinecap: 'butt',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
  longdashdot: {
    strokeDasharray: '10, 5, 1, 5',
    strokeLinecap: 'butt',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
  solid: {
    strokeDasharray: '0',
    strokeLinecap: 'butt',
    strokeWidth: '2',
    lineBorderWidth: '4',
  },
} as const;

function getTitles(layout: Partial<Layout> | undefined) {
  const titleObj = layout?.title;
  const chartTitle = typeof titleObj === 'string' ? titleObj : titleObj?.text ?? '';
  const titleFont = typeof titleObj === 'object' ? titleObj?.font : undefined;
  const titleXAnchor = typeof titleObj === 'object' ? titleObj?.xanchor : undefined;
  const titleYAnchor = typeof titleObj === 'object' ? titleObj?.yanchor : undefined;
  const titlePad = typeof titleObj === 'object' ? titleObj?.pad : undefined;

  const titleStyles: TitleStyles = {
    ...(titleFont ? { titleFont } : {}),
    ...(titleXAnchor ? { titleXAnchor } : {}),
    ...(titleYAnchor ? { titleYAnchor } : {}),
    ...(titlePad ? { titlePad } : {}),
  };

  const titles = {
    chartTitle,
    ...(Object.keys(titleStyles).length > 0 ? { titleStyles } : {}),
    xAxisTitle: typeof layout?.xaxis?.title === 'string' ? layout?.xaxis?.title : layout?.xaxis?.title?.text ?? '',
    yAxisTitle: typeof layout?.yaxis?.title === 'string' ? layout?.yaxis?.title : layout?.yaxis?.title?.text ?? '',
    xAxisAnnotation: chartTitle,
  };
  return titles;
}

const getXAxisTickFormat = (series: Data, layout: Partial<Layout> | undefined) => {
  const xAxis = getXAxisProperties(series, layout);
  if (xAxis?.tickformat) {
    return {
      tickFormat: xAxis?.tickformat,
    };
  }

  return {};
};

const getYAxisTickFormat = (series: Data, layout: Partial<Layout> | undefined) => {
  const yAxis = getYAxisProperties(series, layout);
  if (yAxis?.tickformat) {
    return {
      yAxisTickFormat: d3Format(yAxis?.tickformat),
    };
  }

  return {};
};

const getYMinMaxValues = (series: Data, layout: Partial<Layout> | undefined) => {
  const range = getYAxisProperties(series, layout)?.range;
  if (range && range.length === 2) {
    return {
      yMinValue: range[0],
      yMaxValue: range[1],
    };
  }
  return {};
};

const getXMinMaxValues = (series: Data, layout: Partial<Layout> | undefined) => {
  const range = getXAxisProperties(series, layout)?.range;
  if (range && range.length === 2) {
    return {
      xMinValue: range[0],
      xMaxValue: range[1],
      showRoundOffXTickValues: false,
    };
  }
  return {};
};

const getYAxisProperties = (series: Data, layout: Partial<Layout> | undefined): Partial<LayoutAxis> | undefined => {
  return layout?.yaxis;
};

const getXAxisProperties = (series: Data, layout: Partial<Layout> | undefined): Partial<LayoutAxis> | undefined => {
  return layout?.xaxis;
};

const getFormattedCalloutYData = (
  yVal: string | number,
  yAxisFormat: ReturnType<typeof getYAxisTickFormat>,
): string => {
  if (typeof yAxisFormat?.yAxisTickFormat === 'function' && typeof yVal === 'number') {
    return yAxisFormat.yAxisTickFormat(yVal);
  }
  return formatToLocaleString(yVal) as string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const correctYearMonth = (xValues: Datum[] | Datum[][] | TypedArray): any[] => {
  const presentYear = new Date().getFullYear();
  if (xValues.length > 0 && Array.isArray(xValues[0])) {
    throw new Error('updateXValues:: 2D array not supported');
  }
  const dates = (xValues as Datum[]).map(possiblyMonthValue => {
    const parsedDate = `${possiblyMonthValue} 01, ${presentYear}`;
    return isDate(parsedDate) ? new Date(parsedDate) : null;
  });
  const filteredDateIndexPairs = dates.map((date, index) => [date, index]).filter(([date]) => date !== null) as [
    Date,
    number,
  ][];
  for (let i = filteredDateIndexPairs.length - 1; i > 0; i--) {
    const currentDate = filteredDateIndexPairs[i][0];
    const previousDate = filteredDateIndexPairs[i - 1][0];
    const currentMonth = currentDate.getMonth();
    const previousMonth = previousDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const previousYear = previousDate.getFullYear();
    if (previousMonth >= currentMonth) {
      filteredDateIndexPairs[i - 1][0].setFullYear(currentYear - 1);
    } else if (previousYear > currentYear) {
      filteredDateIndexPairs[i - 1][0].setFullYear(currentYear);
    }
    dates[filteredDateIndexPairs[i - 1][1]] = filteredDateIndexPairs[i - 1][0];
  }
  xValues = (xValues as Datum[]).map((month, index) => {
    if (dates[index] === null) {
      return null;
    }
    return `${month} 01, ${dates[index]!.getFullYear()}`;
  });
  return xValues;
};

const usesSecondaryYScale = (series: Partial<PlotData>, layout: Partial<Layout> | undefined): boolean => {
  return series.yaxis === 'y2' && (layout?.yaxis2?.anchor === 'x' || layout?.yaxis2?.side === 'right');
};

const getSecondaryYAxisValues = (data: Data[], layout: Partial<Layout> | undefined): SecondaryYAxisValues => {
  let containsSecondaryYAxis = false;
  let yMinValue: number | undefined;
  let yMaxValue: number | undefined;
  let allLineSeries = true;

  data.forEach((series: Partial<PlotData>) => {
    if (usesSecondaryYScale(series, layout)) {
      containsSecondaryYAxis = true;

      const yValues = series.y as number[];
      if (yValues) {
        yMinValue = Math.min(...yValues);
        yMaxValue = Math.max(...yValues);
      }

      if (series.type !== 'scatter' || isScatterAreaChart(series)) {
        allLineSeries = false;
      }
    }
  });

  if (!containsSecondaryYAxis) {
    return {};
  }

  if (!allLineSeries) {
    if (typeof yMinValue === 'number') {
      yMinValue = Math.min(yMinValue, 0);
    }
    if (typeof yMaxValue === 'number') {
      yMaxValue = Math.max(yMaxValue, 0);
    }
  }
  if (layout?.yaxis2?.range) {
    yMinValue = layout.yaxis2.range[0];
    yMaxValue = layout.yaxis2.range[1];
  }

  return {
    secondaryYAxistitle:
      typeof layout?.yaxis2?.title === 'string'
        ? layout.yaxis2.title
        : typeof layout?.yaxis2?.title?.text === 'string'
        ? layout.yaxis2.title.text
        : undefined,
    secondaryYScaleOptions: {
      yMinValue,
      yMaxValue,
    },
  };
};

export const _getGaugeAxisColor = (
  colorway: string[] | undefined,
  colorwayType: ColorwayType,
  color: Color | undefined,
  colorMap: React.RefObject<Map<string, string>>,
  isDarkTheme?: boolean,
): string => {
  const extractedColors = extractColor(colorway, colorwayType, color, colorMap, isDarkTheme);
  return resolveColor(extractedColors, 0, '', colorMap, colorway, isDarkTheme);
};

export const resolveXAxisPoint = (
  x: Datum,
  isXYearCategory: boolean,
  isXString: boolean,
  isXDate: boolean,
  isXNumber: boolean,
): string | Date | number => {
  if (x === null || x === undefined) {
    return '';
  }
  if (isXYearCategory) {
    return x.toString();
  }
  if (isXString) {
    if (isXDate) {
      const date = new Date(x as string);
      return date;
    }
    if (isXNumber) {
      return parseFloat(x as string);
    }
    return x;
  }
  return x;
};

/**
 * Formats text values according to the texttemplate specification
 * Supports D3 format specifiers within %{text:format} patterns
 * @param textValue The raw text value to format
 * @param textTemplate The template string (e.g., "%{text:.1f}%", "%{text:.2%}", "%{text:,.0f}")
 * @param index Optional index for array-based templates
 * @returns Formatted text string
 *
 * Examples:
 * - "%{text:.1f}%" → Formats number with 1 decimal place and adds % suffix
 * - "%{text:.2%}" → Formats as percentage with 2 decimal places
 * - "%{text:,.0f}" → Formats with thousands separator and no decimals
 * - "%{text:$,.2f}" → Formats as currency with thousands separator and 2 decimals
 */
const formatTextWithTemplate = (
  textValue: string | number,
  textTemplate?: string | string[],
  index?: number,
): string => {
  if (!textTemplate) {
    return String(textValue);
  }
  const numVal = typeof textValue === 'number' ? textValue : parseFloat(String(textValue));
  if (isNaN(numVal)) {
    return String(textValue);
  }
  const template = typeof textTemplate === 'string' ? textTemplate : textTemplate[index || 0] || '';

  // Match Plotly's texttemplate pattern: %{text:format} or %{text}
  // Can be followed by any literal text like %, $, etc.
  const plotlyPattern = /%\{text(?::([^}]+))?\}(.*)$/;
  const match = template.match(plotlyPattern);

  if (match) {
    const formatSpec = match[1]; // The format specifier (e.g., ".1f", ".2%", ",.0f") or undefined
    const suffix = match[2]; // Any text after the closing brace (e.g., "%", " units")

    // If no format specifier is provided (e.g., %{text}%), try to infer from suffix
    if (!formatSpec) {
      // Check if suffix starts with % - assume simple percentage with 1 decimal
      if (suffix.startsWith('%')) {
        return `${numVal.toFixed(1)}${suffix}`;
      }
      // No format specifier, just return the number with the suffix
      return `${numVal}${suffix}`;
    }

    try {
      // Use D3 format function to apply the format specifier
      const formatter = d3Format(formatSpec);
      const formattedValue = formatter(numVal);
      return `${formattedValue}${suffix}`;
    } catch (error) {
      // Try to extract precision for basic fallback
      const precisionMatch = formatSpec.match(/\.(\d+)[f%]/);
      const precision = precisionMatch ? parseInt(precisionMatch[1], 10) : 2;

      // Check if it's a percentage format
      if (formatSpec.includes('%')) {
        return `${(numVal * 100).toFixed(precision)}%${suffix}`;
      }

      return `${numVal.toFixed(precision)}${suffix}`;
    }
  }

  return String(textValue);
};

/**
 * Extracts unique X-axis categories from Plotly data traces
 * @param data Array of Plotly data traces
 * @returns Array of unique x values
 */
const extractXCategories = (data: Data[] | undefined): Datum[] => {
  return Array.from(
    new Set(
      (data ?? [])
        .flatMap(trace => {
          const xData = (trace as Partial<PlotData>).x;
          if (!xData) {
            return [];
          }
          if (Array.isArray(xData)) {
            return xData.flat().map(x => {
              return x;
            });
          }
          return [];
        })
        .filter(x => x !== undefined && x !== null),
    ),
  );
};

/**
 * Checks if a key should be ignored during normalization
 * @param key The key to check
 * @returns true if the key should be ignored
 */
const shouldIgnoreKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes('style') || lowerKey === 'style') {
    return true;
  }
  // Use regex to match common CSS property patterns
  // (color, fill, stroke, border, background, font, shadow, outline, etc.)
  const cssKeyRegex = new RegExp(
    '^(color|fill|stroke|border|background|font|shadow|outline|margin|padding|gap|align|justify|display|flex|grid|' +
      'text|line|letter|word|vertical|horizontal|overflow|position|top|right|bottom|left|zindex|z-index|opacity|' +
      'filter|clip|cursor|resize|transition|animation|transform|box|column|row|direction|visibility|' +
      'content|width|height|aspect|image|user|pointer|caret|scroll|%)|(-webkit-|-moz-|-ms-|-o-)',
    'i',
  );
  if (cssKeyRegex.test(lowerKey)) {
    return true;
  }
  return false;
};

/**
 * Flattens a nested object into a single level object with dot notation keys
 * @param obj Object to flatten
 * @param prefix Optional prefix for keys
 * @returns Flattened object
 */
const flattenObject = (obj: Record<string, unknown>, prefix: string = ''): Record<string, unknown> => {
  const flattened: Record<string, unknown> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
        // Recursively flatten nested objects
        Object.assign(flattened, flattenObject(value as Record<string, unknown>, newKey));
      } else {
        flattened[newKey] = value;
      }
    }
  }

  return flattened;
};

type PlotlyAnnotation = Partial<Annotations> | null | undefined;

const encodeHtmlEntities = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

type AxisRefType = 'axis' | 'relative' | 'pixel' | undefined;

type ParsedAxisRef = {
  refType: AxisRefType;
  axisId: number;
};

/**
 * Parses Plotly axis references (e.g. `x`, `x2`, `xaxis2`, `paper`, `pixel`, `x domain`) into a ref type + axis id.
 */
const parseAxisRef = (ref: string | undefined, axis: 'x' | 'y'): ParsedAxisRef => {
  if (!ref) {
    return { refType: 'axis', axisId: 1 };
  }

  const normalized = ref.toLowerCase().trim();
  if (normalized === 'pixel') {
    return { refType: 'pixel', axisId: 1 };
  }
  if (normalized === 'paper') {
    return { refType: 'relative', axisId: 1 };
  }
  if (normalized.endsWith(' domain')) {
    return normalized.startsWith(axis) ? { refType: 'relative', axisId: 1 } : { refType: undefined, axisId: 1 };
  }

  const match = normalized.match(/^([xy])(axis)?(\d*)$/);
  if (!match || match[1] !== axis) {
    return { refType: undefined, axisId: 1 };
  }

  const suffix = match[3];
  if (!suffix || suffix === '1') {
    return { refType: 'axis', axisId: 1 };
  }

  const parsed = Number(suffix);
  return { refType: 'axis', axisId: Number.isFinite(parsed) && parsed >= 1 ? parsed : 1 };
};

/**
 * Converts Plotly's bottom-origin relative Y coordinate into the SVG top-origin space used by our overlay.
 */
const transformRelativeYForChart = (value: number | undefined): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (!Number.isFinite(value)) {
    return undefined;
  }
  return 1 - value;
};

const mapHorizontalAlign = (anchor?: string): ChartAnnotationHorizontalAlign | undefined => {
  switch ((anchor ?? '').toLowerCase()) {
    case 'left':
      return 'start';
    case 'center':
      return 'center';
    case 'right':
      return 'end';
    default:
      return undefined;
  }
};

const mapVerticalAlign = (anchor?: string): ChartAnnotationVerticalAlign | undefined => {
  switch ((anchor ?? '').toLowerCase()) {
    case 'top':
      return 'top';
    case 'middle':
      return 'middle';
    case 'bottom':
      return 'bottom';
    default:
      return undefined;
  }
};

const appendPx = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}px`;
  }
  return undefined;
};

/**
 * Maps Plotly's axis reference string to one of our coordinate interpretation modes (axis, relative, or pixel).
 */
const resolveRefType = (ref: string | undefined, axis: 'x' | 'y'): AxisRefType => {
  if (!ref) {
    return 'axis';
  }
  const parsed = parseAxisRef(ref, axis);
  if (parsed.refType !== 'axis') {
    return parsed.refType;
  }
  const normalized = (ref ?? '').toLowerCase().trim();
  const match = normalized.match(/^([xy])(\d*)$/);
  return match && match[1] === axis ? 'axis' : undefined;
};

/**
 * Retrieves the appropriate axis layout section from Plotly's layout given an axis reference alias.
 */
const getAxisLayoutByRef = (
  layout: Partial<Layout> | undefined,
  ref: string | undefined,
  axis: 'x' | 'y',
): Partial<LayoutAxis> | undefined => {
  if (!layout) {
    return undefined;
  }
  const defaultAxisKey = `${axis}axis` as 'xaxis' | 'yaxis';
  const { refType, axisId } = parseAxisRef(ref, axis);

  if (refType !== 'axis' || axisId === 1) {
    return layout[defaultAxisKey];
  }

  const axisKey = `${axis}axis${axisId}` as keyof Layout;
  return layout[axisKey] as Partial<LayoutAxis> | undefined;
};

const convertAnnotationDataValue = (value: unknown, axisType: AxisType): string | number | Date | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (axisType === 'date') {
    const dateValue = value instanceof Date ? value : new Date(value as string | number);
    return Number.isNaN(dateValue.getTime()) ? undefined : dateValue;
  }

  if (axisType === 'linear' || axisType === 'log') {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  // For category-like axes, preserve raw strings (and avoid date parsing heuristics).
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return undefined;
};

const createAnnotationId = (text: string, index: number): string => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized) {
    const slug = normalized
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
    if (slug) {
      return `annotation-${index}-${slug}`;
    }
  }
  return `annotation-${index}`;
};

const DEFAULT_ARROW_OFFSET = -40;

const mapArrowsideToArrow = (annotation: PlotlyAnnotation): ChartAnnotationArrowHead => {
  let includeStart = false;
  let includeEnd = false;

  const arrowSide = typeof annotation?.arrowside === 'string' ? annotation?.arrowside.toLowerCase() : undefined;
  if (arrowSide) {
    includeStart = arrowSide.includes('start');
    includeEnd = arrowSide.includes('end');
  }

  const endHead = toFiniteNumber(annotation?.arrowhead);
  const startHead = toFiniteNumber((annotation as { startarrowhead?: number }).startarrowhead);

  if (endHead !== undefined && endHead > 0) {
    includeEnd = true;
  }
  if (startHead !== undefined && startHead > 0) {
    includeStart = true;
  }

  if (includeStart && includeEnd) {
    return 'both';
  }
  if (includeStart) {
    return 'start';
  }
  if (includeEnd) {
    return 'end';
  }
  return 'none';
};

const mapArrowDashToPattern = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();

  switch (normalized) {
    case 'solid':
      return undefined;
    case 'dot':
      return '1, 5';
    case 'dash':
      return '5, 5';
    case 'longdash':
      return '10, 5';
    case 'dashdot':
      return '5, 5, 1, 5';
    case 'longdashdot':
      return '10, 5, 1, 5';
    default:
      if (/^\d+(\s|,)*\d*$/.test(normalized.replace(/\s+/g, ' '))) {
        return normalized.replace(/\s+/g, ' ');
      }
      return value;
  }
};

type CoordinateRefType = 'axis' | 'relative' | 'pixel';

const mapRefTypeToCoordinateType = (refType: CoordinateRefType): 'data' | 'relative' | 'pixel' => {
  return refType === 'axis' ? 'data' : refType;
};

const normalizeCoordinateValueForType = (
  coordinateType: 'data' | 'relative' | 'pixel',
  value: string | number | Date,
): string | number | Date | undefined => {
  if (coordinateType === 'data') {
    return value;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const getAnnotationCoordinateValue = (
  axis: 'x' | 'y',
  refType: CoordinateRefType,
  annotation: PlotlyAnnotation,
  layout: Partial<Layout> | undefined,
): string | number | Date | undefined => {
  if (refType === 'axis') {
    const axisRef = (axis === 'x' ? annotation?.xref : annotation?.yref) as string | undefined;
    const axisLayout = getAxisLayoutByRef(layout, axisRef, axis);
    const rawValue = axis === 'x' ? annotation?.x : annotation?.y;
    const axisType = (axisLayout?.type as AxisType | undefined) ?? 'category';
    return convertAnnotationDataValue(rawValue, axisType);
  }

  const numericValue = toFiniteNumber(axis === 'x' ? annotation?.x : annotation?.y);
  if (numericValue === undefined) {
    return undefined;
  }

  if (refType === 'relative') {
    return axis === 'y' ? transformRelativeYForChart(numericValue) : numericValue;
  }

  return numericValue;
};

/**
 * Converts a Plotly annotation definition into the internal `ChartAnnotation` format, translating coordinates,
 * layout alignment, styling, and connector metadata while skipping unsupported configurations.
 */
const convertPlotlyAnnotation = (
  annotation: PlotlyAnnotation,
  layout: Partial<Layout> | undefined,
  index: number,
): ChartAnnotation | undefined => {
  if (!annotation || (annotation as { visible?: boolean }).visible === false) {
    return undefined;
  }

  const xRefType = resolveRefType(annotation.xref as string | undefined, 'x');
  const yRefType = resolveRefType(annotation.yref as string | undefined, 'y');

  if (!xRefType || !yRefType) {
    return undefined;
  }

  const xValue = getAnnotationCoordinateValue('x', xRefType, annotation, layout);
  const yValue = getAnnotationCoordinateValue('y', yRefType, annotation, layout);
  if (xValue === undefined || yValue === undefined) {
    return undefined;
  }

  const xCoordinateType = mapRefTypeToCoordinateType(xRefType);
  const yCoordinateType = mapRefTypeToCoordinateType(yRefType);
  const normalizedX = normalizeCoordinateValueForType(xCoordinateType, xValue);
  const normalizedY = normalizeCoordinateValueForType(yCoordinateType, yValue);
  if (normalizedX === undefined || normalizedY === undefined) {
    return undefined;
  }

  const yRefNormalized = typeof annotation.yref === 'string' ? annotation.yref.toLowerCase() : undefined;
  const yAxisProps =
    yCoordinateType === 'data' && yRefNormalized === 'y2' ? ({ yAxis: 'secondary' as const } as const) : undefined;

  let coordinates: ChartAnnotation['coordinates'];

  if (xCoordinateType === 'data' && yCoordinateType === 'data') {
    coordinates = {
      type: 'data',
      x: normalizedX,
      y: normalizedY,
      ...(yAxisProps ?? {}),
    };
  } else if (xCoordinateType === 'relative' && yCoordinateType === 'relative') {
    coordinates = {
      type: 'relative',
      x: normalizedX as number,
      y: normalizedY as number,
    };
  } else if (xCoordinateType === 'pixel' && yCoordinateType === 'pixel') {
    coordinates = {
      type: 'pixel',
      x: normalizedX as number,
      y: normalizedY as number,
    };
  } else {
    coordinates = {
      type: 'mixed',
      xCoordinateType,
      yCoordinateType,
      x: normalizedX,
      y: normalizedY,
      ...(yAxisProps ?? {}),
    };
  }

  const textValue = annotation.text;
  const rawText = textValue === undefined || textValue === null ? '' : String(textValue);
  const encodedText = encodeHtmlEntities(rawText);

  const chartAnnotation: ChartAnnotation = {
    id: createAnnotationId(encodedText, index),
    text: encodedText,
    coordinates,
  };

  const layoutProps: Partial<ChartAnnotationLayoutProps> = {};
  const styleProps: Partial<ChartAnnotationStyleProps> = {};
  const showArrow = annotation.showarrow === undefined ? false : !!annotation.showarrow;

  const clipOnAxis = (annotation as { cliponaxis?: boolean }).cliponaxis;
  if (clipOnAxis !== undefined) {
    layoutProps.clipToBounds = !!clipOnAxis;
  } else if (coordinates.type === 'data') {
    layoutProps.clipToBounds = true;
  }

  const horizontalAlign = mapHorizontalAlign(annotation.xanchor as string | undefined);
  if (horizontalAlign) {
    layoutProps.align = horizontalAlign;
  }

  if (!layoutProps.align) {
    const alignProp = mapHorizontalAlign((annotation as { align?: string }).align);
    if (alignProp) {
      layoutProps.align = alignProp;
    }
  }

  const verticalAlign = mapVerticalAlign(annotation.yanchor as string | undefined);
  if (verticalAlign) {
    layoutProps.verticalAlign = verticalAlign;
  }

  if (!layoutProps.verticalAlign) {
    const valignProp = mapVerticalAlign((annotation as { valign?: string }).valign);
    if (valignProp) {
      layoutProps.verticalAlign = valignProp;
    }
  }

  const offsetXComponents: number[] = [];
  let hasExplicitOffset = false;
  const ax = toFiniteNumber(annotation.ax);
  const axRef = typeof annotation.axref === 'string' ? annotation.axref.toLowerCase() : undefined;
  if (ax !== undefined && (axRef === undefined || axRef === 'pixel')) {
    offsetXComponents.push(ax);
    hasExplicitOffset = true;
  }
  const xShift = toFiniteNumber(annotation.xshift);
  if (xShift !== undefined) {
    offsetXComponents.push(xShift);
    hasExplicitOffset = true;
  }
  if (offsetXComponents.length > 0) {
    const offsetX = offsetXComponents.reduce((sum, value) => sum + value, 0);
    if (offsetX !== 0) {
      layoutProps.offsetX = offsetX;
    }
  }

  const offsetYComponents: number[] = [];
  const ay = toFiniteNumber(annotation.ay);
  const ayRef = typeof annotation.ayref === 'string' ? annotation.ayref.toLowerCase() : undefined;
  if (ay !== undefined && (ayRef === undefined || ayRef === 'pixel')) {
    offsetYComponents.push(ay);
    hasExplicitOffset = true;
  }
  const yShift = toFiniteNumber(annotation.yshift);
  if (yShift !== undefined) {
    offsetYComponents.push(yShift);
    hasExplicitOffset = true;
  }
  if (offsetYComponents.length > 0) {
    const offsetY = offsetYComponents.reduce((sum, value) => sum + value, 0);
    if (offsetY !== 0) {
      layoutProps.offsetY = offsetY;
    }
  }

  if (showArrow && !hasExplicitOffset && layoutProps.offsetY === undefined) {
    layoutProps.offsetY = DEFAULT_ARROW_OFFSET;
  }

  const maxWidth = toFiniteNumber(annotation.width);
  if (maxWidth !== undefined) {
    layoutProps.maxWidth = maxWidth;
  }

  if (annotation.bgcolor) {
    styleProps.backgroundColor = annotation.bgcolor as string;
  }

  if (annotation.bordercolor) {
    styleProps.borderColor = annotation.bordercolor as string;
  }

  const borderWidth = toFiniteNumber(annotation.borderwidth);
  if (borderWidth !== undefined) {
    styleProps.borderWidth = borderWidth;
  }

  const borderPad = appendPx(annotation.borderpad);
  if (borderPad) {
    styleProps.padding = borderPad;
  }

  const opacity = toFiniteNumber(annotation.opacity);
  if (opacity !== undefined) {
    styleProps.opacity = opacity;
  }

  if (annotation.font) {
    const font = annotation.font as {
      color?: string;
      size?: string | number;
      weight?: React.CSSProperties['fontWeight'];
    };
    if (font?.color) {
      styleProps.textColor = font.color;
    }
    const fontSize = appendPx(font?.size);
    if (fontSize) {
      styleProps.fontSize = fontSize;
    }
    if (font?.weight) {
      styleProps.fontWeight = font.weight;
    }
  }

  const textAngle = (annotation as { textangle?: number | string })?.textangle;
  if (typeof textAngle === 'number' && !Number.isNaN(textAngle)) {
    styleProps.rotation = textAngle;
  } else if (typeof textAngle === 'string' && textAngle.toLowerCase() !== 'auto') {
    const parsedAngle = Number(textAngle);
    if (!Number.isNaN(parsedAngle)) {
      styleProps.rotation = parsedAngle;
    }
  }
  if (Object.keys(layoutProps).length > 0) {
    chartAnnotation.layout = layoutProps;
  }

  if (Object.keys(styleProps).length > 0) {
    chartAnnotation.style = styleProps;
  }

  if (showArrow) {
    const arrowColor = (annotation.arrowcolor as string) ?? styleProps.textColor;
    const arrowWidth = toFiniteNumber(annotation.arrowwidth);
    const endPadding = toFiniteNumber(annotation.standoff);
    const startPadding = toFiniteNumber((annotation as { startstandoff?: number }).startstandoff);
    const dashPattern = mapArrowDashToPattern((annotation as { arrowdash?: string }).arrowdash);

    chartAnnotation.connector = {
      ...(arrowColor ? { strokeColor: arrowColor } : {}),
      ...(arrowWidth !== undefined ? { strokeWidth: arrowWidth } : {}),
      ...(endPadding !== undefined ? { endPadding: Math.max(endPadding, 0) } : {}),
      ...(startPadding !== undefined ? { startPadding: Math.max(startPadding, 0) } : {}),
      ...(dashPattern ? { dashArray: dashPattern } : {}),
      arrow: mapArrowsideToArrow(annotation),
    };
  }

  return chartAnnotation;
};

const getChartAnnotationsFromLayout = (
  data: Data[] | undefined,
  layout: Partial<Layout> | undefined,
  isMultiPlot: boolean,
): ChartAnnotation[] | undefined => {
  if (isMultiPlot || !layout?.annotations) {
    return undefined;
  }

  // Infer axis types when they are not explicitly set.
  // This is needed so annotation coordinate parsing can correctly treat values as 'date' vs 'category'
  // (for example, bar chart category axes with date-like strings).
  const inferredLayout = (() => {
    if (!data || !isArrayOrTypedArray(data) || data.length === 0) {
      return layout;
    }

    const valuesByAxisKey = new Map<keyof Layout, Datum[]>();
    const axesExpectingCategories = new Set<keyof Layout>();

    data.forEach(series => {
      const trace = series as Partial<PlotData>;
      const axisIds = getAxisIds(trace);

      if (trace.type === 'bar') {
        const categoryAxisLetter = trace.orientation === 'h' ? 'y' : 'x';
        axesExpectingCategories.add(getAxisKey(categoryAxisLetter, axisIds[categoryAxisLetter]));
      }

      (['x', 'y'] as const).forEach(axLetter => {
        const coords = trace[axLetter];
        if (!coords || !isArrayOrTypedArray(coords)) {
          return;
        }

        const axisKey = getAxisKey(axLetter, axisIds[axLetter]);
        const existing = valuesByAxisKey.get(axisKey) ?? [];
        (coords as Datum[] | TypedArray).forEach(val => {
          if (!isInvalidValue(val)) {
            existing.push(val as Datum);
          }
        });
        valuesByAxisKey.set(axisKey, existing);
      });
    });

    let nextLayout: Partial<Layout> | undefined;

    valuesByAxisKey.forEach((values, axisKey) => {
      const currentAxis = layout?.[axisKey];
      const currentType = currentAxis?.type;
      if (['linear', 'log', 'date', 'category'].includes(currentType ?? '')) {
        return;
      }

      let inferredType: AxisType | undefined;
      if (axesExpectingCategories.has(axisKey) || isYearArray(values)) {
        inferredType = 'category';
      } else if (isDateArray(values)) {
        inferredType = 'date';
      }

      if (!inferredType) {
        return;
      }

      if (!nextLayout) {
        nextLayout = { ...layout };
      }

      nextLayout[axisKey] = {
        ...(currentAxis ?? {}),
        type: inferredType,
      };
    });

    return nextLayout ?? layout;
  })();

  const annotationsArray = Array.isArray(layout.annotations) ? layout.annotations : [layout.annotations];
  const converted = annotationsArray
    .map((annotation, index) => convertPlotlyAnnotation(annotation as PlotlyAnnotation, inferredLayout, index))
    .filter((annotation): annotation is ChartAnnotation => annotation !== undefined);

  return converted.length > 0 ? converted : undefined;
};

/**
 * Normalizes an array of objects by flattening nested structures and creating grouped data
 * Uses json_normalize approach with D3 color detection and filtering
 * @param data Array of objects to normalize
 * @returns Object containing traces for grouped vertical bar chart
 */
export const normalizeObjectArrayForGVBC = (
  data: Array<Record<string, unknown>>,
  xLabels?: string[],
): { traces: Array<Record<string, unknown>>; x: string[] } => {
  if (!data || data.length === 0) {
    return { traces: [], x: [] };
  }

  // Use provided xLabels if available, otherwise default to Item 1, Item 2, ...
  const x = xLabels && xLabels.length === data.length ? xLabels : data.map((_, index) => `Item ${index + 1}`);

  // First, flatten all objects and collect all unique keys, excluding style keys
  const flattenedObjects = data.map((item, index) => {
    if (typeof item === 'object' && item !== null) {
      const flattened = flattenObject(item);
      // Only keep keys where the value is numeric (number or numeric string) and not a style key
      const filtered: Record<string, unknown> = {};
      Object.keys(flattened).forEach(key => {
        const value = flattened[key];
        if (!shouldIgnoreKey(key) && (typeof value === 'number' || (typeof value === 'string' && isNumber(value)))) {
          filtered[key] = value;
        }
      });
      return filtered;
    } else if (typeof item === 'number' || (typeof item === 'string' && isNumber(item))) {
      // Only keep primitive numeric values
      return { [x[index] || `item_${index}`]: item };
    } else {
      // Non-numeric primitive, ignore by returning empty object
      return {};
    }
  });

  // Collect all unique keys across all objects
  const allKeys = new Set<string>();
  flattenedObjects.forEach(obj => {
    Object.keys(obj).forEach(key => allKeys.add(key));
  });

  // Create traces for each key (property)
  const traces: Array<Record<string, unknown>> = [];

  allKeys.forEach(key => {
    const yValues: number[] = [];
    let hasValidData = false;
    let isNumericData = false;

    flattenedObjects.forEach((obj, index) => {
      const value = obj[key];
      if (typeof value === 'number') {
        yValues.push(value);
        hasValidData = true;
        isNumericData = true;
      } else if (typeof value === 'string' && isNumber(value)) {
        yValues.push(parseFloat(value));
        hasValidData = true;
        isNumericData = true;
      }
    });

    // Only create trace if we have valid numeric data
    if (hasValidData && isNumericData) {
      const trace: Record<string, unknown> = {
        type: 'bar',
        name: key,
        x,
        y: yValues,
      };

      traces.push(trace);
    }
  });

  return { traces, x };
};

export const transformPlotlyJsonToAnnotationChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  _colorMap: React.RefObject<Map<string, string>>,
  _colorwayType: ColorwayType,
  _isDarkTheme?: boolean,
): AnnotationOnlyChartProps => {
  const annotations = getChartAnnotationsFromLayout(input.data, input.layout, isMultiPlot) ?? [];
  const titles = getTitles(input.layout);
  const layoutTitle = titles.chartTitle || undefined;

  const layoutWithMeta = input.layout as Partial<Layout> & { meta?: { description?: string } };
  const description =
    typeof layoutWithMeta?.meta?.description === 'string' ? layoutWithMeta.meta.description : undefined;

  const width = typeof input.layout?.width === 'number' ? input.layout.width : undefined;
  const height = typeof input.layout?.height === 'number' ? input.layout.height : undefined;
  const paperBackgroundColor = typeof input.layout?.paper_bgcolor === 'string' ? input.layout.paper_bgcolor : undefined;
  const plotBackgroundColor = typeof input.layout?.plot_bgcolor === 'string' ? input.layout.plot_bgcolor : undefined;
  const fontColor = typeof input.layout?.font?.color === 'string' ? input.layout.font.color : undefined;
  const fontFamily = typeof input.layout?.font?.family === 'string' ? input.layout.font.family : undefined;
  const margin = input.layout?.margin;

  return {
    annotations,
    chartTitle: layoutTitle,
    description,
    width,
    height,
    paperBackgroundColor,
    plotBackgroundColor,
    fontColor,
    fontFamily,
    margin,
  };
};

export const transformPlotlyJsonToDonutProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): DonutChartProps => {
  const firstData = input.data[0] as Partial<PieData>;

  // extract colors for each series only once
  // use piecolorway if available
  // otherwise, default to colorway from template
  const colors: string[] | string | null | undefined = extractColor(
    input.layout?.piecolorway ?? input.layout?.template?.layout?.colorway,
    colorwayType,
    input.layout?.piecolorway ?? firstData?.marker?.colors,
    colorMap,
    isDarkTheme,
    true,
  );
  const mapLegendToDataPoint: Record<string, ChartDataPoint> = {};
  // clear colorMap for donut chart to reassign colors as the colorMap initially gets assigned by
  // getAllupLegendsProps function without sorting labels by value
  colorMap.current!.clear();

  // Sort labels by value descending before mapping
  if (firstData.labels && firstData.values) {
    const markerColors = (firstData.marker?.colors as unknown as string[]) || undefined;
    const hasMarkerColors = Array.isArray(markerColors) && markerColors.length >= firstData.labels.length;
    const labelValuePairs = firstData.labels.map((label, index) => ({
      label,
      value: getNumberAtIndexOrDefault(firstData.values, index),
      index,
      color: hasMarkerColors ? markerColors[index] : undefined,
    }));
    // Filter out invalid values
    const validPairs = labelValuePairs.filter(pair => !isInvalidValue(pair.value));
    // Sort descending by value; when marker colors are present, keep color attached to the label
    validPairs.sort((a, b) => (b.value as number) - (a.value as number));
    validPairs.forEach((pair, sortedIdx) => {
      const legend = `${pair.label}`;
      const color: string =
        pair.color ??
        resolveColor(
          colors,
          sortedIdx,
          legend,
          colorMap,
          input.layout?.piecolorway ?? input.layout?.template?.layout?.colorway,
          isDarkTheme,
          true,
        );

      if (!mapLegendToDataPoint[legend]) {
        mapLegendToDataPoint[legend] = {
          legend,
          data: pair.value,
          color,
        };
      } else {
        mapLegendToDataPoint[legend].data! += pair.value as number;
      }
    });
  }

  const width: number = input.layout?.width ?? 440;
  const height: number = input.layout?.height ?? 220;
  const hideLabels: boolean = firstData.textinfo
    ? !['value', 'percent', 'label+percent', 'percent+label'].includes(firstData.textinfo)
    : false;
  const donutMarginHorizontal: number = hideLabels ? 0 : 80;
  const donutMarginVertical: number = 40 + (hideLabels ? 0 : 40);
  const innerRadius: number = firstData.hole
    ? firstData.hole * (Math.min(width - donutMarginHorizontal, height - donutMarginVertical) / 2)
    : MIN_DONUT_RADIUS;
  const { chartTitle, titleStyles } = getTitles(input.layout);
  // Build anticlockwise order by keeping the first item, reversing the rest
  const legends = Object.keys(mapLegendToDataPoint);
  const reorderedEntries =
    legends.length > 1
      ? ([
          [legends[0], mapLegendToDataPoint[legends[0]]],
          ...legends
            .slice(1)
            .reverse()
            .map(key => [key, mapLegendToDataPoint[key]] as const),
        ] as ReadonlyArray<readonly [string, ChartDataPoint]>)
      : legends.map(key => [key, mapLegendToDataPoint[key]] as const);

  return {
    data: {
      chartTitle,
      chartData: reorderedEntries.map(([, v]) => v as ChartDataPoint),
    },
    hideLegend: isMultiPlot || input.layout?.showlegend === false,
    width: input.layout?.width,
    height,
    innerRadius,
    hideLabels,
    showLabelsInPercent: firstData.textinfo
      ? ['percent', 'label+percent', 'percent+label'].includes(firstData.textinfo)
      : true,
    roundCorners: true,
    order: 'sorted',
    ...(titleStyles ? { titleStyles } : {}),
  } as DonutChartProps;
};

export const transformPlotlyJsonToVSBCProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
  fallbackVSBC?: boolean,
): VerticalStackedBarChartProps => {
  const mapXToDataPoints: { [key: string]: VerticalStackedChartProps } = {};
  let yMaxValue = 0;
  let yMinValue = 0;
  const secondaryYAxisValues = getSecondaryYAxisValues(input.data, input.layout);
  const { legends, hideLegend } = getLegendProps(input.data, input.layout, isMultiPlot);
  let colorScale: ((value: number) => string) | undefined = undefined;
  const yAxisTickFormat = getYAxisTickFormat(input.data[0], input.layout);
  const resolveXValue = getAxisValueResolver(getAxisType(input.data, getAxisObjects(input.data, input.layout).x));
  input.data.forEach((series: Partial<PlotData>, index1: number) => {
    colorScale = createColorScale(input.layout, series, colorScale);
    // extract bar colors for each series only once
    const extractedBarColors = extractColor(
      input.layout?.template?.layout?.colorway,
      colorwayType,
      series.marker?.color,
      colorMap,
      isDarkTheme,
    ) as string[] | string | undefined;
    // extract line colors for each series only once
    const extractedLineColors = extractColor(
      input.layout?.template?.layout?.colorway,
      colorwayType,
      series.line?.color,
      colorMap,
      isDarkTheme,
    ) as string[] | string | undefined;

    const validXYRanges = getValidXYRanges(series, resolveXValue);
    validXYRanges.forEach(([rangeStart, rangeEnd], rangeIdx) => {
      const rangeXValues = series.x!.slice(rangeStart, rangeEnd);
      const rangeYValues = series.y!.slice(rangeStart, rangeEnd);
      const textValues = Array.isArray(series.text)
        ? series.text.slice(rangeStart, rangeEnd)
        : typeof series.text === 'string'
        ? series.text
        : undefined;

      (rangeXValues as Datum[]).forEach((x: string | number, index2: number) => {
        if (!mapXToDataPoints[x]) {
          mapXToDataPoints[x] = {
            xAxisPoint: resolveXValue(x)!,
            chartData: [],
            lineData: [],
          };
        }
        const legend: string = legends[index1];
        // resolve color for each legend's bars from the colorscale or extracted colors
        const color = colorScale
          ? colorScale(
              isArrayOrTypedArray(series.marker?.color)
                ? ((series.marker?.color as Color[])?.[index2 % (series.marker?.color as Color[]).length] as number)
                : 0,
            )
          : resolveColor(
              extractedBarColors,
              index2,
              legend,
              colorMap,
              input.layout?.template?.layout?.colorway,
              isDarkTheme,
            );
        const opacity = getOpacity(series, index2);
        const yVal: number | string = rangeYValues[index2] as number | string;
        const yAxisCalloutData = getFormattedCalloutYData(yVal, yAxisTickFormat);
        let barLabel = Array.isArray(textValues) ? textValues[index2] : textValues;

        // Apply texttemplate formatting if specified
        if (barLabel && series.texttemplate) {
          barLabel = formatTextWithTemplate(barLabel, series.texttemplate, index2);
        }
        if (series.type === 'bar') {
          mapXToDataPoints[x].chartData.push({
            legend,
            data: yVal,
            color: rgb(color).copy({ opacity }).formatHex8() ?? color,
            yAxisCalloutData,
            ...(barLabel ? { barLabel: String(barLabel) } : {}),
          });
          if (typeof yVal === 'number') {
            yMaxValue = Math.max(yMaxValue, yVal);
          }
        } else if (series.type === 'scatter' || !!fallbackVSBC) {
          const lineColor = resolveColor(
            extractedLineColors,
            index1,
            legend,
            colorMap,
            input.layout?.template?.layout?.colorway,
            isDarkTheme,
          );
          const lineOptions = !series.mode?.includes('text') ? getLineOptions(series.line) : undefined;
          const legendShape = getLegendShape(series);
          mapXToDataPoints[x].lineData!.push({
            legend: legend + (validXYRanges.length > 1 ? `.${rangeIdx + 1}` : ''),
            legendShape,
            y: yVal,
            color: rgb(lineColor).copy({ opacity }).formatHex8() ?? color,
            lineOptions: {
              ...(lineOptions ?? {}),
              mode: series.mode,
            },
            useSecondaryYScale: usesSecondaryYScale(series, input.layout),
            yAxisCalloutData,
          });
          if (!usesSecondaryYScale(series, input.layout) && typeof yVal === 'number') {
            yMaxValue = Math.max(yMaxValue, yVal);
            yMinValue = Math.min(yMinValue, yVal);
          }
        }
        yMaxValue = Math.max(yMaxValue, yVal as number);
      });
    });
  });

  const xCategories = extractXCategories(input.data);

  (input.layout?.shapes ?? [])
    .filter(shape => shape.type === 'line')
    .forEach((shape, shapeIdx) => {
      const lineColor = shape.line?.color;
      const resolveX = (val: Datum) => {
        if (shape.xref === 'x domain') {
          if (val === 0) {
            return xCategories[0];
          }
          if (val === 1) {
            return xCategories[xCategories.length - 1];
          }
        }
        if (typeof val === 'number' && Array.isArray(xCategories)) {
          if (xCategories[val] !== undefined) {
            return xCategories[val];
          } else {
            return xCategories[shapeIdx];
          }
        }
        return val;
      };

      const x0Key = resolveX(shape.x0!);
      const x1Key = resolveX(shape.x1!);
      const resolveY = (val: Datum) => {
        if (shape.yref === 'paper') {
          if (val === 0) {
            return yMinValue;
          }
          if (val === 1) {
            return yMaxValue;
          }
          if (typeof val === 'number') {
            return yMinValue + val * (yMaxValue - yMinValue);
          }
          return val;
        }
        return val;
      };

      const y0Val = resolveY(shape.y0!);
      const y1Val = resolveY(shape.y1!);
      if (mapXToDataPoints[x0Key as string]) {
        mapXToDataPoints[x0Key as string].lineData!.push({
          legend: `Reference_${shapeIdx}`,
          y: y0Val as string,
          color: rgb(lineColor!).formatHex8() ?? lineColor,
          lineOptions: getLineOptions(shape.line),
          useSecondaryYScale: false,
        });
      }

      if (mapXToDataPoints[x1Key as string]) {
        mapXToDataPoints[x1Key as string].lineData!.push({
          legend: `Reference_${shapeIdx}`,
          y: y1Val as string,
          color: rgb(lineColor!).formatHex8() ?? lineColor,
          lineOptions: getLineOptions(shape.line),
          useSecondaryYScale: false,
        });
      }
    });

  const vsbcData = Object.values(mapXToDataPoints);
  const annotations = getChartAnnotationsFromLayout(input.data, input.layout, isMultiPlot);

  return {
    data: vsbcData,
    width: input.layout?.width,
    height: input.layout?.height ?? 350,
    barWidth: 'auto',
    yMaxValue,
    yMinValue,
    mode: 'plotly',
    ...secondaryYAxisValues,
    hideTickOverlap: true,
    barGapMax: 2,
    hideLegend,
    roundCorners: true,
    showYAxisLables: true,
    noOfCharsToTruncate: 20,
    showYAxisLablesTooltip: true,
    roundedTicks: true,
    ...getXMinMaxValues(input.data[0], input.layout),
    ...getTitles(input.layout),
    ...getXAxisTickFormat(input.data[0], input.layout),
    ...yAxisTickFormat,
    ...getAxisCategoryOrderProps(input.data, input.layout),
    ...getBarProps(input.data, input.layout),
    ...getYMinMaxValues(input.data[0], input.layout),
    ...getAxisTickProps(input.data, input.layout),
    ...(annotations ? { annotations } : {}),
  };
};

export const transformPlotlyJsonToGVBCProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): GroupedVerticalBarChartProps => {
  // Handle object arrays in y values by normalizing the data first
  let processedInput = { ...input };

  // Check if any bar traces have object arrays as y values
  const hasObjectArrayData = input.data.some(
    (series: Partial<PlotData>) => series.type === 'bar' && isObjectArray(series.y),
  );

  if (hasObjectArrayData) {
    // Process each trace that has object array y values
    const processedData = input.data
      .map((series: Partial<PlotData>, index: number) => {
        if (series.type === 'bar' && isObjectArray(series.y)) {
          // Normalize the object array to create multiple traces for GVBC
          const { traces } = normalizeObjectArrayForGVBC(
            series.y as unknown as Array<Record<string, unknown>>,
            Array.isArray(series.x) ? (series.x as string[]) : undefined,
          );

          // Return all the new traces, each representing a property from the objects
          return traces.map((trace: Record<string, unknown>) => ({
            ...trace,
            // Copy other properties from the original series if needed
            marker: series.marker,
          }));
        }
        return [series];
      })
      .flat();

    processedInput = {
      ...input,
      data: processedData,
    };
  }
  const gvbcDataV2: GroupedVerticalBarChartProps['dataV2'] = [];
  const secondaryYAxisValues = getSecondaryYAxisValues(processedInput.data, processedInput.layout);
  const { legends, hideLegend } = getLegendProps(processedInput.data, processedInput.layout, isMultiPlot);

  let colorScale: ((value: number) => string) | undefined = undefined;
  const yAxisTickFormat = getYAxisTickFormat(processedInput.data[0], processedInput.layout);
  processedInput.data.forEach((series: Partial<PlotData>, index1: number) => {
    colorScale = createColorScale(processedInput.layout, series, colorScale);
    const legend: string = legends[index1];
    const legendShape = getLegendShape(series);

    if (series.type === 'bar') {
      // extract bar colors for each series only once
      const extractedBarColors = extractColor(
        processedInput.layout?.template?.layout?.colorway,
        colorwayType,
        series.marker?.color,
        colorMap,
        isDarkTheme,
      ) as string[] | string | undefined;

      gvbcDataV2.push({
        type: 'bar',
        legend,
        key: legend,
        data: (series.x as Datum[])
          .map((x, xIndex) => {
            if (isInvalidValue(x) || isInvalidValue(series.y?.[xIndex])) {
              return;
            }

            // resolve color for each legend's bars from the colorscale or extracted colors
            const color = colorScale
              ? colorScale(
                  isArrayOrTypedArray(series.marker?.color)
                    ? ((series.marker?.color as Color[])?.[xIndex % (series.marker?.color as Color[]).length] as number)
                    : 0,
                )
              : resolveColor(
                  extractedBarColors,
                  xIndex,
                  legend,
                  colorMap,
                  processedInput.layout?.template?.layout?.colorway,
                  isDarkTheme,
                );
            const opacity = getOpacity(series, xIndex);
            const yVal = series.y![xIndex] as number;
            // Extract text value for barLabel
            let barLabel = Array.isArray(series.text) ? series.text[xIndex] : series.text;

            // Apply texttemplate formatting if specified
            if (barLabel && series.texttemplate) {
              barLabel = formatTextWithTemplate(barLabel, series.texttemplate, xIndex);
            }

            return {
              x: x!.toString(),
              y: yVal,
              yAxisCalloutData: getFormattedCalloutYData(yVal, yAxisTickFormat),
              color: rgb(color).copy({ opacity }).formatHex8() ?? color,
              ...(barLabel ? { barLabel: String(barLabel) } : {}),
            };
          })
          .filter(item => typeof item !== 'undefined'),
        useSecondaryYScale: usesSecondaryYScale(series, processedInput.layout),
      });
    } else if (series.type === 'scatter') {
      // extract line colors for each series only once
      const extractedLineColors = extractColor(
        processedInput.layout?.template?.layout?.colorway,
        colorwayType,
        series.line?.color,
        colorMap,
        isDarkTheme,
      ) as string[] | string | undefined;
      const lineColor = resolveColor(
        extractedLineColors,
        index1,
        legend,
        colorMap,
        processedInput.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      const lineOptions = getLineOptions(series.line);
      const opacity = getOpacity(series, index1);
      const validXYRanges = getValidXYRanges(series);

      validXYRanges.forEach(([rangeStart, rangeEnd]) => {
        const rangeXValues = series.x!.slice(rangeStart, rangeEnd) as Datum[];
        const rangeYValues = series.y!.slice(rangeStart, rangeEnd) as Datum[];

        gvbcDataV2.push({
          type: 'line',
          legend,
          legendShape,
          data: rangeXValues.map((x, i: number) => {
            const yVal = rangeYValues[i] as number;
            return {
              x: x!.toString(),
              y: yVal,
              yAxisCalloutData: getFormattedCalloutYData(yVal, yAxisTickFormat),
            };
          }),
          color: rgb(lineColor).copy({ opacity }).formatHex8() ?? lineColor,
          lineOptions: {
            ...(lineOptions ?? {}),
            mode: series.mode,
          },
          useSecondaryYScale: usesSecondaryYScale(series, processedInput.layout),
        });
      });
    }
  });

  const annotations = getChartAnnotationsFromLayout(processedInput.data, processedInput.layout, isMultiPlot);

  return {
    dataV2: gvbcDataV2,
    width: processedInput.layout?.width,
    height: processedInput.layout?.height ?? 350,
    barWidth: 'auto',
    mode: 'plotly',
    ...secondaryYAxisValues,
    hideTickOverlap: true,
    hideLegend,
    roundCorners: true,
    showYAxisLables: true,
    roundedTicks: true,
    ...getXMinMaxValues(processedInput.data[0], processedInput.layout),
    ...getTitles(processedInput.layout),
    ...getAxisCategoryOrderProps(processedInput.data, processedInput.layout),
    ...getYMinMaxValues(processedInput.data[0], processedInput.layout),
    ...getXAxisTickFormat(processedInput.data[0], processedInput.layout),
    ...yAxisTickFormat,
    ...getBarProps(processedInput.data, processedInput.layout),
    ...getAxisTickProps(processedInput.data, processedInput.layout),
    ...(annotations ? { annotations } : {}),
  };
};

export const transformPlotlyJsonToVBCProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): VerticalBarChartProps => {
  const vbcData: VerticalBarChartDataPoint[] = [];
  const { legends, hideLegend } = getLegendProps(input.data, input.layout, isMultiPlot);
  let colorScale: ((value: number) => string) | undefined = undefined;

  input.data.forEach((series: Partial<PlotData>, seriesIdx: number) => {
    if (!series.x) {
      return;
    }

    colorScale = createColorScale(input.layout, series, colorScale);

    // extract colors for each series only once
    const extractedColors = extractColor(
      input.layout?.template?.layout?.colorway,
      colorwayType,
      series.marker?.color,
      colorMap,
      isDarkTheme,
    ) as string[] | string | undefined;
    const xValues: (string | number)[] = [];
    const yValues: number[] = [];
    series.x.forEach((xVal, index) => {
      const yVal = getNumberAtIndexOrDefault(series.y, index) ?? 0;
      if (isInvalidValue(xVal)) {
        return;
      }

      xValues.push(xVal as string | number);
      yValues.push(yVal as number);
    });

    const isXString = isStringArray(xValues);
    // TODO: In case of a single bin, add an empty bin of the same size to prevent the
    // default bar width from being used and ensure the bar spans the full intended range.
    const xBins = createBins(xValues, series.xbins?.start, series.xbins?.end, series.xbins?.size);
    const yBins: number[][] = xBins.map(() => []);
    let total = 0;

    xValues.forEach((xVal, index) => {
      const binIdx = findBinIndex(xBins, xVal, isXString);
      if (binIdx !== -1) {
        yBins[binIdx].push(yValues[index]);
      }
    });

    const y = yBins.map(bin => {
      const yVal = calculateHistFunc(series.histfunc, bin);
      total += yVal;
      return yVal;
    });

    xBins.forEach((bin, index) => {
      const legend: string = legends[seriesIdx];
      // resolve color for each legend's bars from the colorscale or extracted colors
      const color = colorScale
        ? colorScale(
            isArrayOrTypedArray(series.marker?.color)
              ? ((series.marker?.color as Color[])?.[index % (series.marker?.color as Color[]).length] as number)
              : 0,
          )
        : resolveColor(extractedColors, index, legend, colorMap, input.layout?.template?.layout?.colorway, isDarkTheme);
      const opacity = getOpacity(series, index);
      const yVal = calculateHistNorm(
        series.histnorm,
        y[index],
        total,
        isXString ? bin.length : getBinSize(bin as Bin<number, number>),
      );

      // Handle text values and texttemplate formatting for histogram bins
      let barLabel = Array.isArray(series.text) ? series.text[index] : series.text;
      if (barLabel && series.texttemplate) {
        barLabel = formatTextWithTemplate(barLabel, series.texttemplate, index);
      }

      vbcData.push({
        x: isXString ? bin.join(', ') : getBinCenter(bin as Bin<number, number>),
        y: yVal,
        legend,
        color: rgb(color).copy({ opacity }).formatHex8() ?? color,
        ...(isXString
          ? {}
          : { xAxisCalloutData: `[${(bin as Bin<number, number>).x0} - ${(bin as Bin<number, number>).x1})` }),
        ...(barLabel ? { barLabel: String(barLabel) } : {}),
      });
    });
  });

  const annotations = getChartAnnotationsFromLayout(input.data, input.layout, isMultiPlot);
  return {
    data: vbcData,
    width: input.layout?.width,
    height: input.layout?.height ?? 350,
    mode: 'histogram',
    hideTickOverlap: true,
    maxBarWidth: 50,
    hideLegend,
    roundCorners: true,
    showYAxisLables: true,
    roundedTicks: true,
    ...getXMinMaxValues(input.data[0], input.layout),
    ...getTitles(input.layout),
    ...getYMinMaxValues(input.data[0], input.layout),
    ...getAxisCategoryOrderProps(input.data, input.layout),
    ...(annotations ? { annotations } : {}),
  };
};

export const transformPlotlyJsonToAreaChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): AreaChartProps => {
  return transformPlotlyJsonToScatterTraceProps(
    input,
    isMultiPlot,
    'area',
    colorMap,
    colorwayType,
    isDarkTheme,
  ) as AreaChartProps;
};

export const transformPlotlyJsonToLineChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): LineChartProps => {
  return transformPlotlyJsonToScatterTraceProps(
    input,
    isMultiPlot,
    'line',
    colorMap,
    colorwayType,
    isDarkTheme,
  ) as LineChartProps;
};

export const transformPlotlyJsonToScatterChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): LineChartProps => {
  return transformPlotlyJsonToScatterTraceProps(
    input,
    isMultiPlot,
    'scatter',
    colorMap,
    colorwayType,
    isDarkTheme,
  ) as ScatterChartProps;
};

const mapColorFillBars = (layout: Partial<Layout> | undefined) => {
  if (!Array.isArray(layout?.shapes)) {
    return [];
  }

  return layout.shapes
    .filter((shape: Partial<Shape>) => shape.type === 'rect')
    .map((shape: { x0?: Datum; x1?: Datum; fillcolor?: string }) => {
      //colorFillbars doesn't support string dates or categories
      if (typeof shape.x0 === 'string' || typeof shape.x1 === 'string') {
        return null;
      }
      return {
        color: shape.fillcolor!,
        data: [{ startX: shape.x0, endX: shape.x1 }],
        applyPattern: false,
      };
    });
};

const transformPlotlyJsonToScatterTraceProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  chartType: ScatterChartTypes,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): LineChartProps | AreaChartProps | ScatterChartProps => {
  const isScatterMarkers = [
    'text',
    'markers',
    'text+markers',
    'markers+text',
    'lines+markers',
    'markers+line',
    'text+lines+markers',
    'lines+markers+text',
  ].includes((input.data[0] as PlotData)?.mode);
  const isAreaChart = chartType === 'area';
  const isScatterChart = chartType === 'scatter';
  const secondaryYAxisValues = getSecondaryYAxisValues(input.data, input.layout);
  let mode: string = 'tonexty';
  const { legends, hideLegend } = getLegendProps(input.data, input.layout, isMultiPlot);
  const yAxisTickFormat = getYAxisTickFormat(input.data[0], input.layout);
  const resolveXValue = getAxisValueResolver(getAxisType(input.data, getAxisObjects(input.data, input.layout).x));
  const chartData: LineChartPoints[] = input.data
    .map((series: Partial<PlotData>, index: number) => {
      const colors = isScatterMarkers
        ? series?.mode?.includes('line')
          ? series.line?.color
          : series.marker?.color
        : series.line?.color;
      // extract colors for each series only once
      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        colors,
        colorMap,
        isDarkTheme,
      ) as string[] | string | undefined;
      const xValues = series.x as Datum[];
      const legend: string = legends[index];
      // resolve color for each legend's lines from the extracted colors
      const seriesColor = resolveColor(
        extractedColors,
        index,
        legend,
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      const seriesOpacity = getOpacity(series, index);
      mode = series.fill === 'tozeroy' ? 'tozeroy' : 'tonexty';
      // if mode contains 'text', we prioritize showing the text over curving the line
      const lineOptions =
        !series.mode?.includes('text') && series.type !== 'scatterpolar' ? getLineOptions(series.line) : undefined;
      const legendShape = getLegendShape(series);

      const validXYRanges = getValidXYRanges(series, resolveXValue);
      return validXYRanges.map(([rangeStart, rangeEnd], rangeIdx) => {
        const rangeXValues = xValues.slice(rangeStart, rangeEnd);
        const rangeYValues = series.y!.slice(rangeStart, rangeEnd);
        const markerSizes = isArrayOrTypedArray(series.marker?.size)
          ? (series.marker!.size as number[]).slice(rangeStart, rangeEnd)
          : [];
        const markerColors = isArrayOrTypedArray(series.marker?.color)
          ? (series.marker!.color as string[]).slice(rangeStart, rangeEnd)
          : Array.isArray(series.marker?.color)
          ? (series.marker!.color as string[]).slice(rangeStart, rangeEnd)
          : undefined;
        const textValues = Array.isArray(series.text) ? series.text.slice(rangeStart, rangeEnd) : undefined;

        return {
          legend,
          legendShape,
          data: rangeXValues.map((x, i: number) => ({
            x: resolveXValue(x),
            y: rangeYValues[i],
            ...(Array.isArray(series.marker?.size)
              ? { markerSize: markerSizes[i] }
              : typeof series.marker?.size === 'number'
              ? { markerSize: series.marker.size }
              : {}),
            ...(markerColors ? { markerColor: markerColors[i] } : {}),
            ...(textValues ? { text: textValues[i] } : {}),
            yAxisCalloutData: getFormattedCalloutYData(rangeYValues[i] as number, yAxisTickFormat),
          })),
          color: rgb(seriesColor).copy({ opacity: seriesOpacity }).formatHex8() ?? seriesColor,
          lineOptions: {
            ...(lineOptions ?? {}),
            mode: series.type !== 'scatterpolar' ? series.mode : 'scatterpolar',
            // originXOffset is not typed on Layout, but may be present in input.layout as a part of projection of
            // scatter polar coordingates to cartesian coordinates
            ...(series.type === 'scatterpolar'
              ? {
                  originXOffset: (input.layout as { __polarOriginX?: number } | undefined)?.__polarOriginX,
                  direction: input.layout?.polar?.angularaxis?.direction,
                  rotation: input.layout?.polar?.angularaxis?.rotation,
                  axisLabel: (series as { __axisLabel: string[] }).__axisLabel
                    ? (series as { __axisLabel: string[] }).__axisLabel
                    : {},
                  fill: series.fill,
                }
              : {}),
          },
          useSecondaryYScale: usesSecondaryYScale(series, input.layout),
        } as LineChartPoints;
      });
    })
    .flat();

  const xMinValue = chartData[0]?.data[0]?.x;
  const xMaxValue = chartData[0]?.data[chartData[0].data.length - 1]?.x;
  const yMinValue = chartData[0]?.data[0]?.y;
  const yMaxValue = chartData[0]?.data[chartData[0].data.length - 1]?.y;
  const xCategories = extractXCategories(input.data);
  const lineShape: LineChartPoints[] | ScatterChartPoints[] = (input.layout?.shapes ?? [])
    .filter(shape => shape.type === 'line')
    .map((shape, shapeIdx) => {
      const lineColor = shape.line?.color;
      const resolveX = (val: Datum) => {
        if (typeof val === 'number' && Array.isArray(xCategories)) {
          if (xCategories[val] !== undefined) {
            return xCategories[val];
          } else {
            return xCategories[shapeIdx];
          }
        }
        if (shape.xref === 'paper') {
          if (val === 0) {
            return xMinValue;
          }
          if (val === 1) {
            return xMaxValue;
          }
          if (typeof val === 'number' && typeof xMinValue === 'number' && typeof xMaxValue === 'number') {
            return xMinValue + val * (xMaxValue - xMinValue);
          }
          return val;
        }
        return val;
      };

      const resolveY = (val: Datum) => {
        if (shape.yref === 'paper') {
          if (val === 0) {
            return yMinValue;
          }
          if (val === 1) {
            return yMaxValue;
          }
          if (typeof val === 'number') {
            return yMinValue + val * (yMaxValue - yMinValue);
          }
          return val;
        }
        return val;
      };

      return {
        legend: `Reference_${shapeIdx}`,
        data: [
          { x: resolveXValue(resolveX(shape.x0!)), y: resolveY(shape.y0!) },
          { x: resolveXValue(resolveX(shape.x1!)), y: resolveY(shape.y1!) },
        ],
        color: rgb(lineColor!).formatHex8() ?? lineColor,
        lineOptions: getLineOptions(shape.line),
        useSecondaryYScale: false,
      } as LineChartPoints | ScatterChartPoints;
    });

  const yMinMax = getYMinMaxValues(input.data[0], input.layout);
  if (yMinMax.yMinValue === undefined && yMinMax.yMaxValue === undefined) {
    const yMinMaxValues = findNumericMinMaxOfY(chartData);
    yMinMax.yMinValue = yMinMaxValues.startValue;
    yMinMax.yMaxValue = yMinMaxValues.endValue;
  }
  const numDataPoints = chartData.reduce((total, lineChartPoints) => total + lineChartPoints.data.length, 0);

  const chartProps: ChartProps = {
    lineChartData: [...chartData, ...(lineShape as LineChartPoints[])],
  };

  const scatterChartProps: ChartProps = {
    scatterChartData: [...chartData, ...(lineShape as ScatterChartPoints[])],
  };

  const annotations = getChartAnnotationsFromLayout(input.data, input.layout, isMultiPlot);

  const commonProps = {
    supportNegativeData: true,
    ...secondaryYAxisValues,
    width: input.layout?.width,
    height: input.layout?.height ?? 350,
    hideTickOverlap: true,
    hideLegend,
    useUTC: false,
    optimizeLargeData: numDataPoints > 1000,
    showYAxisLables: true,
    roundedTicks: true,
    ...getXMinMaxValues(input.data[0], input.layout),
    ...getTitles(input.layout),
    ...getXAxisTickFormat(input.data[0], input.layout),
    ...yAxisTickFormat,
    ...getAxisScaleTypeProps(input.data, input.layout),
    ...getAxisTickProps(input.data, input.layout),
    ...(annotations ? { annotations } : {}),
  };

  if (isAreaChart) {
    return {
      data: chartProps,
      mode,
      ...commonProps,
    } as AreaChartProps;
  } else {
    return {
      data: isScatterChart ? scatterChartProps : chartProps,
      ...commonProps,
      ...yMinMax,
      ...(isScatterChart
        ? {
            showYAxisLablesTooltip: true,
            ...getAxisCategoryOrderProps(input.data, input.layout),
          }
        : {}),
      ...(!isScatterChart &&
        (() => {
          const bars = mapColorFillBars(input.layout);
          return bars && !bars.includes(null) ? { colorFillBars: bars } : {};
        })()),
    } as LineChartProps;
  }
};

export const transformPlotlyJsonToHorizontalBarWithAxisProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): HorizontalBarChartWithAxisProps => {
  const { legends, hideLegend } = getLegendProps(input.data, input.layout, isMultiPlot);
  let colorScale: ((value: number) => string) | undefined = undefined;
  const chartData: HorizontalBarChartWithAxisDataPoint[] = input.data
    .map((series: Partial<PlotData>, index: number) => {
      colorScale = createColorScale(input.layout, series, colorScale);
      // extract colors for each series only once
      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        series.marker?.color,
        colorMap,
        isDarkTheme,
      ) as string[] | string | undefined;
      const legend = legends[index];
      return (series.y as Datum[])
        .map((yValue, i: number) => {
          if (isInvalidValue(yValue)) {
            return null;
          }

          // resolve color for each legend's bars from the colorscale or extracted colors
          const color = colorScale
            ? colorScale(
                isArrayOrTypedArray(series.marker?.color)
                  ? ((series.marker?.color as Color[])?.[i % (series.marker?.color as Color[]).length] as number)
                  : 0,
              )
            : resolveColor(extractedColors, i, legend, colorMap, input.layout?.template?.layout?.colorway, isDarkTheme);
          const opacity = getOpacity(series, i);

          return {
            x: isInvalidValue(series.x?.[i]) ? 0 : series.x![i],
            y: yValue,
            legend,
            color: rgb(color).copy({ opacity }).formatHex8() ?? color,
            ...(series.text?.[i] ? { barLabel: series.text[i].toString() } : {}),
          } as HorizontalBarChartWithAxisDataPoint;
        })
        .filter(point => point !== null) as HorizontalBarChartWithAxisDataPoint[];
    })
    .flat();

  const chartHeight: number = input.layout?.height ?? 450;
  const margin: number = input.layout?.margin?.l ?? 0;
  const padding: number = input.layout?.margin?.pad ?? 0;
  const availableHeight: number = chartHeight - margin - padding;
  const numberOfRows = new Set(chartData.map(d => d.y)).size || 1;
  const scalingFactor = 0.01;
  const gapFactor = 1 / (1 + scalingFactor * numberOfRows);
  const barHeight = availableHeight / (numberOfRows * (1 + gapFactor));

  return {
    data: chartData,
    secondaryYAxistitle:
      typeof input.layout?.yaxis2?.title === 'string'
        ? input.layout?.yaxis2?.title
        : input.layout?.yaxis2?.title?.text || '',
    barHeight,
    showYAxisLables: true,
    height: chartHeight,
    width: input.layout?.width,
    hideTickOverlap: true,
    noOfCharsToTruncate: 20,
    showYAxisLablesTooltip: true,
    hideLegend,
    roundCorners: true,
    roundedTicks: true,
    ...getXMinMaxValues(input.data[0], input.layout),
    ...getTitles(input.layout),
    ...getAxisCategoryOrderProps(input.data, input.layout),
    ...getBarProps(input.data, input.layout, true),
    ...getAxisTickProps(input.data, input.layout),
  };
};

export const transformPlotlyJsonToGanttChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): GanttChartProps => {
  const data = input.data.filter((series: Partial<PlotData>) => series.type !== 'scatter' || series.mode !== 'markers');
  const { legends, hideLegend } = getLegendProps(data, input.layout, isMultiPlot);
  let colorScale: ((value: number) => string) | undefined = undefined;
  const xAxisType = getAxisType(data, getAxisObjects(data, input.layout).x);
  const resolveXValue = getAxisValueResolver(xAxisType, parseLocalDate);
  const resolveGanttXValue = (value: Datum) => {
    const resolvedValue = resolveXValue(value);
    return typeof resolvedValue === 'string' ? 0 : resolvedValue ?? 0;
  };
  const isXDate = xAxisType === 'date';
  const ganttData: GanttChartDataPoint[] = [];
  data.forEach((series: Partial<PlotData>, index: number) => {
    const legend = legends[index];

    if (series.type === 'bar') {
      colorScale = createColorScale(input.layout, series, colorScale);

      // extract colors for each series only once
      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        series.marker?.color,
        colorMap,
        isDarkTheme,
      ) as string[] | string | undefined;

      (series.y as Datum[]).forEach((yVal, i: number) => {
        if (isInvalidValue(yVal)) {
          return;
        }
        // resolve color for each legend's bars from the colorscale or extracted colors
        const color = colorScale
          ? colorScale(
              isArrayOrTypedArray(series.marker?.color)
                ? ((series.marker?.color as Color[])?.[i % (series.marker?.color as Color[]).length] as number)
                : 0,
            )
          : resolveColor(extractedColors, i, legend, colorMap, input.layout?.template?.layout?.colorway, isDarkTheme);
        const opacity = getOpacity(series, i);
        const base = +resolveGanttXValue((isArrayOrTypedArray(series.base) ? series.base![i] : series.base) as Datum);
        const xVal = +resolveGanttXValue(series.x?.[i] as Datum);

        ganttData.push({
          x: {
            start: isXDate ? new Date(base) : base,
            end: isXDate ? new Date(base + xVal) : base + xVal,
          },
          y: yVal as string | number,
          legend,
          color: rgb(color).copy({ opacity }).formatHex8() ?? color,
        });
      });
    } else if (series.type === 'scatter' && series.mode === 'none' && series.fill === 'toself') {
      for (let i = 0; i < series.y!.length; i += 5) {
        if (isInvalidValue(series.y![i]) || isInvalidValue(series.y![i + 3])) {
          continue;
        }

        const x0 = resolveGanttXValue(series.x![i] as Datum);
        const x1 = resolveGanttXValue(series.x![i + 1] as Datum);
        const y0 = series.y![i] as number;
        const y1 = series.y![i + 3] as number;

        ganttData.push({
          x: {
            start: x0,
            end: x1,
          },
          y: Math.round((y0 + y1) / 2),
          legend,
          color: series.fillcolor,
        });
      }
    }
  });

  return {
    data: ganttData,
    showYAxisLables: true,
    height: input.layout?.height ?? 350,
    width: input.layout?.width,
    hideTickOverlap: true,
    hideLegend,
    noOfCharsToTruncate: 20,
    showYAxisLablesTooltip: true,
    roundCorners: true,
    useUTC: false,
    ...getTitles(input.layout),
    ...getAxisCategoryOrderProps(data, input.layout),
    ...getBarProps(data, input.layout, true),
    ...getAxisTickProps(data, input.layout),
  };
};

export const transformPlotlyJsonToHeatmapProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): HeatMapChartProps => {
  const firstData = input.data[0] as Partial<PlotData>;
  const heatmapDataPoints: HeatMapChartDataPoint[] = [];
  let zMin = Number.POSITIVE_INFINITY;
  let zMax = Number.NEGATIVE_INFINITY;

  // Build a 2D array of annotations based on their grid position
  const annotationGrid: (string | undefined)[][] = [];
  const rawAnnotations = input.layout?.annotations;

  if (rawAnnotations) {
    const annotationsArray = Array.isArray(rawAnnotations) ? rawAnnotations : [rawAnnotations];

    // Collect all unique x and y values from valid annotations
    const xSet = new Set<number>();
    const ySet = new Set<number>();
    const validAnnotations: Array<{ x: number; y: number; text: string }> = [];

    annotationsArray.forEach((a: PlotlyAnnotation) => {
      if (
        a &&
        typeof a.x === 'number' &&
        typeof a.y === 'number' &&
        typeof a.text === 'string' &&
        (a.xref === 'x' || a.xref === undefined) &&
        (a.yref === 'y' || a.yref === undefined)
      ) {
        xSet.add(a.x);
        ySet.add(a.y);
        validAnnotations.push({ x: a.x, y: a.y, text: cleanText(a.text) });
      }
    });

    if (validAnnotations.length > 0) {
      // Get sorted unique x and y values
      const xValues = Array.from(xSet).sort((a, b) => a - b);
      const yValues = Array.from(ySet).sort((a, b) => a - b);

      // Initialize 2D grid and populate
      validAnnotations.forEach(annotation => {
        const xIdx = xValues.indexOf(annotation.x);
        const yIdx = yValues.indexOf(annotation.y);
        if (!annotationGrid[yIdx]) {
          annotationGrid[yIdx] = [];
        }
        annotationGrid[yIdx][xIdx] = annotation.text;
      });
    }
  }

  // Helper function to get annotation from 2D grid by index
  const getAnnotationByIndex = (xIdx: number, yIdx: number): string | undefined => annotationGrid[yIdx]?.[xIdx];

  if (firstData.type === 'histogram2d') {
    const xValues: (string | number)[] = [];
    const yValues: (string | number)[] = [];
    const zValues: number[] = [];
    firstData.x?.forEach((xVal, index) => {
      const zVal = getNumberAtIndexOrDefault(firstData.z, index) ?? 0;
      if (isInvalidValue(xVal) || isInvalidValue(firstData.y?.[index])) {
        return;
      }

      xValues.push(xVal as string | number);
      yValues.push(firstData.y![index] as string | number);
      zValues.push(zVal as number);
    });

    const isXString = isStringArray(xValues);
    const isYString = isStringArray(yValues);
    const xBins = createBins(xValues, firstData.xbins?.start, firstData.xbins?.end, firstData.xbins?.size);
    const yBins = createBins(yValues, firstData.ybins?.start, firstData.ybins?.end, firstData.ybins?.size);
    const zBins: number[][][] = yBins.map(() => xBins.map(() => []));
    let total = 0;

    xValues.forEach((xVal, index) => {
      const xBinIdx = findBinIndex(xBins, xVal, isXString);
      const yBinIdx = findBinIndex(yBins, yValues[index], isYString);

      if (xBinIdx !== -1 && yBinIdx !== -1) {
        zBins[yBinIdx][xBinIdx].push(zValues[index]);
      }
    });

    const z = zBins.map(row => {
      return row.map(bin => {
        const zVal = calculateHistFunc(firstData.histfunc, bin);
        total += zVal;
        return zVal;
      });
    });

    xBins.forEach((xBin, xIdx) => {
      yBins.forEach((yBin, yIdx) => {
        const zVal = calculateHistNorm(
          firstData.histnorm,
          z[yIdx][xIdx],
          total,
          isXString ? xBin.length : getBinSize(xBin as Bin<number, number>),
          isYString ? yBin.length : getBinSize(yBin as Bin<number, number>),
        );

        const annotationText = getAnnotationByIndex(xIdx, yIdx);

        heatmapDataPoints.push({
          x: isXString ? xBin.join(', ') : getBinCenter(xBin as Bin<number, number>),
          y: isYString ? yBin.join(', ') : getBinCenter(yBin as Bin<number, number>),
          value: zVal,
          rectText: annotationText || zVal,
        });

        if (typeof zVal === 'number') {
          zMin = Math.min(zMin, zVal);
          zMax = Math.max(zMax, zVal);
        }
      });
    });
  } else {
    // If x and y are not provided, generate indices based on z dimensions
    const zArray = firstData.z as number[][];
    const xValues = firstData.x as Datum[] | undefined;
    const yValues = firstData.y as Datum[] | undefined;

    // Determine the dimensions from z array
    const yLength = zArray?.length ?? 0;
    const xLength = zArray?.[0]?.length ?? 0;

    // Use provided x/y values or generate indices
    const xData = xValues ?? Array.from({ length: xLength }, (_, i) => i);
    const yData = yValues ?? Array.from({ length: yLength }, (_, i) => yLength - 1 - i);

    xData.forEach((xVal, xIdx: number) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yData.forEach((yVal: any, yIdx: number) => {
        const zVal = zArray?.[yIdx]?.[xIdx];

        const annotationText = getAnnotationByIndex(xIdx, yIdx);

        heatmapDataPoints.push({
          x: input.layout?.xaxis?.type === 'date' ? (xVal as Date) : xVal ?? 0,
          y: input.layout?.yaxis?.type === 'date' ? (yVal as Date) : yVal,
          value: zVal,
          rectText: annotationText || zVal,
        });

        if (typeof zVal === 'number') {
          zMin = Math.min(zMin, zVal);
          zMax = Math.max(zMax, zVal);
        }
      });
    });
  }

  const heatmapData: HeatMapChartData = {
    legend: firstData.name ?? '',
    data: heatmapDataPoints,
    value: 0,
  };

  // Initialize domain and range to default values
  const defaultDomain = [zMin, (zMax + zMin) / 2, zMax];
  const defaultRange = [
    getColorFromToken(DataVizPalette.color1),
    getColorFromToken(DataVizPalette.color2),
    getColorFromToken(DataVizPalette.color3),
  ];

  let colorscale =
    firstData?.colorscale ??
    input.layout?.colorscale ??
    input.layout?.coloraxis?.colorscale ??
    input.layout?.template?.layout?.colorscale ??
    (firstData.type === 'histogram2d' && input.layout?.template?.data?.histogram2d?.[0]?.colorscale) ??
    input.layout?.template?.data?.heatmap?.[0]?.colorscale;

  // determine if the types diverging, sequential or sequentialminus are present in colorscale
  if (
    colorscale &&
    typeof colorscale === 'object' &&
    ('diverging' in colorscale || 'sequential' in colorscale || 'sequentialminus' in colorscale)
  ) {
    const isDivergent = zMin < 0 && zMax > 0; // Data spans both positive and negative values
    const isSequential = zMin >= 0; // Data is entirely positive
    const isSequentialMinus = zMax <= 0; // Data is entirely negative

    if (isDivergent) {
      colorscale = colorscale?.diverging;
    } else if (isSequential) {
      colorscale = colorscale?.sequential;
    } else if (isSequentialMinus) {
      colorscale = colorscale?.sequentialminus;
    }
  }

  const domainValuesForColorScale: number[] = Array.isArray(colorscale)
    ? (colorscale as Array<[number, string]>).map(arr => arr[0] * (zMax - zMin) + zMin)
    : defaultDomain;

  const rangeValuesForColorScale: string[] = Array.isArray(colorscale)
    ? (colorscale as Array<[number, string]>).map(arr => arr[1])
    : defaultRange;

  return {
    data: [heatmapData],
    domainValuesForColorScale,
    rangeValuesForColorScale,
    hideLegend: true,
    showYAxisLables: true,
    sortOrder: 'none',
    width: input.layout?.width,
    height: input.layout?.height ?? 350,
    hideTickOverlap: true,
    noOfCharsToTruncate: 20,
    showYAxisLablesTooltip: true,
    ...getTitles(input.layout),
    ...getAxisCategoryOrderProps([firstData], input.layout),
    ...getAxisTickProps(input.data, input.layout),
  };
};

export const transformPlotlyJsonToSankeyProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): SankeyChartProps => {
  const { link, node } = input.data[0] as SankeyData;
  const validLinks = (link?.value ?? [])
    .map((val: number, index: number) => {
      if (isInvalidValue(val) || isInvalidValue(link?.source?.[index]) || isInvalidValue(link?.target?.[index])) {
        return null;
      }

      return {
        value: val,
        source: link?.source![index],
        target: link?.target![index],
      };
    })
    // Filter out negative nodes, unequal nodes and self-references (circular links)
    .filter(x => x !== null && x.source >= 0 && x.target >= 0 && x.source !== x.target);

  const extractedNodeColors = extractColor(
    input.layout?.template?.layout?.colorway,
    colorwayType,
    node?.color,
    colorMap,
    isDarkTheme,
  );
  const extractedLinkColors = extractColor(
    input.layout?.template?.layout?.colorway,
    colorwayType,
    link?.color,
    colorMap,
    isDarkTheme,
  );

  const sankeyChartData = {
    nodes: node.label?.map((label: string, index: number) => {
      const color = resolveColor(
        extractedNodeColors,
        index,
        label,
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );

      return {
        nodeId: index,
        name: label,
        color,
      };
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    links: validLinks.map((validLink: any, index: number) => {
      const color = resolveColor(
        extractedLinkColors,
        index,
        validLink.target,
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      return {
        ...validLink,
        color,
      };
    }),
  } as SankeyChartData;

  // const styles: SankeyChartProps['styles'] = {
  //   root: {
  //     ...(input.layout?.font?.size ? { fontSize: input.layout.font?.size } : {}),
  //   },
  // };

  const { chartTitle, titleStyles } = getTitles(input.layout);

  return {
    data: {
      chartTitle,
      SankeyChartData: sankeyChartData,
    },
    width: input.layout?.width,
    height: input.layout?.height ?? 468,
    // TODO
    // styles,
    hideLegend: isMultiPlot || input.layout?.showlegend === false,
    ...(titleStyles ? { titleStyles } : {}),
  } as SankeyChartProps;
};

export const transformPlotlyJsonToGaugeProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): GaugeChartProps => {
  const firstData = input.data[0] as PlotData;
  const stepsColors = firstData.gauge?.steps ? firstData.gauge.steps.map(step => step.color) : undefined;
  const extractedColors = extractColor(
    input.layout?.template?.layout?.colorway,
    colorwayType,
    stepsColors,
    colorMap,
    isDarkTheme,
  );

  const segments = firstData.gauge?.steps?.length
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      firstData.gauge.steps.map((step: any, index: number): GaugeChartSegment => {
        const legend = step.name || `Segment ${index + 1}`;
        const color = resolveColor(
          extractedColors,
          index,
          legend,
          colorMap,
          input.layout?.template?.layout?.colorway,
          isDarkTheme,
        );
        return {
          legend,
          size: step.range?.[1] - step.range?.[0],
          color,
        };
      })
    : [
        {
          legend: 'Current',
          size: firstData.value ?? 0 - (firstData.gauge?.axis?.range?.[0] ?? 0),
          color: _getGaugeAxisColor(
            input.layout?.template?.layout?.colorway,
            colorwayType,
            firstData.gauge?.axis?.color,
            colorMap,
            isDarkTheme,
          ),
        },
        {
          legend: 'Target',
          size: (firstData.gauge?.axis?.range?.[1] ?? 100) - (firstData.value ?? 0),
          color: DataVizPalette.disabled,
        },
      ];

  let sublabel: string | undefined;
  let sublabelColor: string | undefined;
  if (firstData.delta?.reference) {
    const diff = firstData.value - firstData.delta.reference;
    if (diff >= 0) {
      sublabel = `\u25B2 ${diff}`;
      const extractedIncreasingDeltaColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        firstData.delta?.increasing?.color,
        colorMap,
        isDarkTheme,
      );
      const color = resolveColor(
        extractedIncreasingDeltaColors,
        0,
        '',
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      sublabelColor = color;
    } else {
      sublabel = `\u25BC ${Math.abs(diff)}`;
      const extractedDecreasingDeltaColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        firstData.delta?.decreasing?.color,
        colorMap,
        isDarkTheme,
      );
      const color = resolveColor(
        extractedDecreasingDeltaColors,
        0,
        '',
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      sublabelColor = color;
    }
  }

  const styles = {
    sublabel: sublabelColor,
  };

  const { chartTitle, titleStyles } = getTitles(input.layout);

  return {
    segments,
    chartValue: firstData.value ?? 0,
    chartTitle,
    sublabel,
    // range values can be null
    minValue: typeof firstData.gauge?.axis?.range?.[0] === 'number' ? firstData.gauge?.axis?.range?.[0] : undefined,
    maxValue: typeof firstData.gauge?.axis?.range?.[1] === 'number' ? firstData.gauge?.axis?.range?.[1] : undefined,
    chartValueFormat: () => firstData.value?.toString() ?? '',
    width: input.layout?.width,
    height: input.layout?.height ?? 220,
    // TODO
    // styles,
    variant: firstData.gauge?.steps?.length ? 'multiple-segments' : 'single-segment',
    styles,
    roundCorners: true,
    ...(titleStyles ? { titleStyles } : {}),
  } as GaugeChartProps;
};

const cleanText = (text: string): string => {
  return text
    .replace(/&lt;[^&]*?&gt;/g, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;br&gt;|\\u003cbr\\u003e|<br>/gi, '')
    .replace(/\$[^$]*\$/g, '$')
    .trim();
};

const formatValue = (
  value: string | number | boolean | null,
  colIndex: number,
  cells: TableData['cells'],
): string | number | boolean | null => {
  if (value === null || typeof value === 'boolean') {
    return value;
  }

  const formatStr = Array.isArray(cells!.format) ? cells!.format[colIndex] : cells!.format;
  const prefix = Array.isArray(cells!.prefix) ? cells!.prefix[colIndex] : cells!.prefix;
  const suffix = Array.isArray(cells!.suffix) ? cells!.suffix[colIndex] : cells!.suffix;
  let formatted = value;
  if (typeof value === 'number') {
    if (typeof formatStr === 'string') {
      try {
        formatted = d3Format(formatStr)(value);
      } catch {
        formatted = formatScientificLimitWidth(value);
      }
    } else {
      formatted = formatScientificLimitWidth(value);
    }
  }
  return `${prefix ?? ''}${formatted}${suffix ?? ''}`;
};

function resolveCellStyle<T>(raw: T | T[] | T[][] | undefined, rowIndex: number, colIndex: number): T | undefined {
  if (Array.isArray(raw)) {
    const rowEntry = raw[colIndex] ?? raw[0];
    if (Array.isArray(rowEntry)) {
      return rowEntry[rowIndex] ?? rowEntry[0];
    }
    return rowEntry;
  }
  return raw;
}

function mergeCells(tableCells?: TableData['cells'], templateCells?: TableData['cells']): TableData['cells'] {
  return {
    values: tableCells?.values ?? templateCells?.values ?? [],
    align: tableCells?.align ?? templateCells?.align,

    fill: {
      ...(templateCells?.fill ?? {}),
      ...(tableCells?.fill ?? {}),
    },

    font: {
      ...(templateCells?.font ?? {}),
      ...(tableCells?.font ?? {}),
    },

    format: tableCells?.format ?? templateCells?.format,
    prefix: tableCells?.prefix ?? templateCells?.prefix,
    suffix: tableCells?.suffix ?? templateCells?.suffix,
  };
}

export const transformPlotlyJsonToChartTableProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): ChartTableProps => {
  const tableData = input.data[0] as TableData;

  const normalizeHeaders = (
    values: (string | number | boolean | null)[] | (string | number | boolean | null)[][],
    header: TableData['header'],
  ): { value: string | number | boolean | null; style?: React.CSSProperties }[] => {
    const cleanedValues: (string | number | boolean | null)[] = Array.isArray(values[0])
      ? (values as string[][]).map(row =>
          row
            .map(cell => cleanText(cell))
            .filter(Boolean)
            .join(' '),
        )
      : (values as string[]).map(cell => cleanText(cell));

    return cleanedValues.map((value, colIndex) => {
      //headers are at first row only
      const rowIndex = 0;
      const fontColor = resolveCellStyle(header?.font?.color, rowIndex, colIndex) as string | undefined;
      const fontSize = resolveCellStyle(header?.font?.size, rowIndex, colIndex) as number | undefined;
      const backgroundColor = resolveCellStyle(header?.fill?.color, rowIndex, colIndex) as string | undefined;
      const textAlign = resolveCellStyle(header?.align, rowIndex, colIndex) as
        | React.CSSProperties['textAlign']
        | undefined;

      const style: React.CSSProperties = {
        ...(typeof fontColor === 'string' ? { color: fontColor } : {}),
        ...(typeof fontSize === 'number' ? { fontSize } : {}),
        ...(typeof backgroundColor === 'string' ? { backgroundColor } : {}),
        ...(textAlign ? { textAlign } : {}),
      };

      return { value, style };
    });
  };
  const columns = tableData.cells?.values ?? [];
  const cells = mergeCells(tableData.cells, input.layout?.template?.data?.table?.[0]?.cells);
  const rows = columns[0].map((_, rowIndex: number) =>
    columns.map((col, colIndex) => {
      const cellValue = col[rowIndex];
      const cleanValue = typeof cellValue === 'string' ? cleanText(cellValue) : cellValue;

      const formattedValue =
        typeof cleanValue === 'string' || typeof cleanValue === 'number'
          ? formatValue(cleanValue, colIndex, cells)
          : cleanValue;

      const fontColor = resolveCellStyle(cells?.font?.color, rowIndex, colIndex) as string | undefined;
      const fontSize = resolveCellStyle(cells?.font?.size, rowIndex, colIndex) as number | undefined;
      const backgroundColor = resolveCellStyle(cells?.fill?.color, rowIndex, colIndex) as string | undefined;
      const textAlign = resolveCellStyle(cells?.align, rowIndex, colIndex) as
        | React.CSSProperties['textAlign']
        | undefined;

      const style: React.CSSProperties = {
        ...(fontColor ? { color: fontColor } : {}),
        ...(typeof fontSize === 'number' ? { fontSize } : {}),
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(textAlign ? { textAlign } : {}),
      };

      return {
        value: formattedValue,
        style,
      };
    }),
  );

  const styles: ChartTableProps['styles'] = {
    root: {
      ...(input.layout?.font?.size ? { fontSize: input.layout.font.size } : {}),
    },
  };

  const templateHeader = input.layout?.template?.data?.table?.[0]?.header;
  const tableHeader = tableData.header;

  const header = {
    align: tableHeader?.align ?? templateHeader?.align,
    fill: {
      ...(templateHeader?.fill ?? {}),
      ...(tableHeader?.fill ?? {}),
    },
    font: {
      ...(templateHeader?.font ?? {}),
      ...(tableHeader?.font ?? {}),
    },
    values: tableHeader?.values ?? templateHeader?.values ?? [],
  };

  const { chartTitle, titleStyles } = getTitles(input.layout);

  return {
    headers: normalizeHeaders(tableData.header?.values ?? [], header),
    rows,
    width: input.layout?.width,
    height: input.layout?.height,
    styles,
    chartTitle,
    ...(titleStyles ? { titleStyles } : {}),
  } as ChartTableProps;
};

function getCategoriesAndValues(series: Partial<PlotData>): {
  categories: (string | number)[];
  values: (string | number)[];
} {
  const orientation = series.orientation || 'h';
  const y = series.labels ?? series.y ?? series.stage;
  const x = series.values ?? series.x ?? series.value;
  const xIsString = isStringArray(x as Datum[] | Datum[][] | TypedArray | undefined);
  const yIsString = isStringArray(y as Datum[] | Datum[][] | TypedArray | undefined);
  const xIsNumber = isNumberArray(x as Datum[] | Datum[][] | TypedArray | undefined);
  const yIsNumber = isNumberArray(y as Datum[] | Datum[][] | TypedArray | undefined);

  // Helper to ensure array of (string | number)
  const toArray = (arr: unknown): (string | number)[] => {
    if (Array.isArray(arr)) {
      return arr as (string | number)[];
    }
    if (typeof arr === 'string' || typeof arr === 'number') {
      return [arr];
    }
    return [];
  };

  if (orientation === 'h') {
    if (yIsString && xIsNumber) {
      return { categories: toArray(y), values: toArray(x) };
    } else if (xIsString && yIsNumber) {
      return { categories: toArray(x), values: toArray(y) };
    } else {
      return { categories: yIsString ? toArray(y) : toArray(x), values: yIsString ? toArray(x) : toArray(y) };
    }
  } else {
    if (xIsString && yIsNumber) {
      return { categories: toArray(x), values: toArray(y) };
    } else if (yIsString && xIsNumber) {
      return { categories: toArray(y), values: toArray(x) };
    } else {
      return { categories: xIsString ? toArray(x) : toArray(y), values: xIsString ? toArray(y) : toArray(x) };
    }
  }
}

export const transformPlotlyJsonToFunnelChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): FunnelChartProps => {
  const funnelData: FunnelChartDataPoint[] = [];

  // Determine if data is stacked based on multiple series with multiple values per series
  const isStacked =
    input.data.length > 1 &&
    input.data.every((series: Partial<PlotData>) => {
      const values = series.values ?? series.x ?? series.value;
      const labels = series.labels ?? series.y ?? series.stage;
      return Array.isArray(labels) && Array.isArray(values) && values.length > 1 && labels.length > 1;
    });

  if (isStacked) {
    // Assign a color per series/category and use it for all subValues of that category
    const seriesColors: Record<string, string> = {};
    input.data.forEach((series: Partial<PlotData>, seriesIdx: number) => {
      const category = series.name || `Category ${seriesIdx + 1}`;
      // Use the same color for this category across all stages
      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        series.marker?.colors ?? series.marker?.color,
        colorMap,
        isDarkTheme,
      );
      // Always use the first color for the series/category
      const color = resolveColor(
        extractedColors,
        0,
        category,
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      seriesColors[category] = color;

      const labels = series.labels ?? series.y ?? series.stage;
      const values = series.values ?? series.x ?? series.value;

      if (!isArrayOrTypedArray(labels) || !isArrayOrTypedArray(values)) {
        return;
      }
      if (labels && isArrayOrTypedArray(labels) && labels.length > 0) {
        (labels as (string | number)[]).forEach((label: string, i: number) => {
          const stageIndex = funnelData.findIndex(stage => stage.stage === label);
          const valueNum = Number((values as (string | number)[])[i]);
          if (isNaN(valueNum)) {
            return;
          }
          if (stageIndex === -1) {
            funnelData.push({
              stage: label,
              subValues: [{ category, value: valueNum, color }],
            });
          } else {
            funnelData[stageIndex].subValues!.push({ category, value: valueNum, color });
          }
        });
      }
    });
  } else {
    // Non-stacked data handling (multiple series with single-value arrays)
    input.data.forEach((series: Partial<PlotData>, seriesIdx: number) => {
      const { categories, values } = getCategoriesAndValues(series);

      if (!isArrayOrTypedArray(categories) || !isArrayOrTypedArray(values)) {
        return;
      }

      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        series.marker?.colors ?? series.marker?.color,
        colorMap,
        isDarkTheme,
      );

      categories.forEach((label: string, i: number) => {
        const color = resolveColor(
          extractedColors,
          i,
          label,
          colorMap,
          input.layout?.template?.layout?.colorway,
          isDarkTheme,
        );
        const valueNum = Number(values[i]);
        if (isNaN(valueNum)) {
          return;
        }
        funnelData.push({
          stage: label,
          value: valueNum,
          color,
        });
      });
    });
  }
  const { chartTitle, titleStyles } = getTitles(input.layout);

  return {
    data: funnelData,
    chartTitle,
    width: input.layout?.width,
    height: input.layout?.height,
    orientation: (input.data[0] as Partial<PlotData>)?.orientation === 'v' ? 'horizontal' : 'vertical',
    hideLegend: isMultiPlot || input.layout?.showlegend === false,
    ...(titleStyles ? { titleStyles } : {}),
  } as FunnelChartProps;
};

export const transformPlotlyJsonToPolarChartProps = (
  input: PlotlySchema,
  isMultiPlot: boolean,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  isDarkTheme?: boolean,
): PolarChartProps => {
  const polarData: PolarChartProps['data'] = [];
  const { legends, hideLegend } = getLegendProps(input.data, input.layout, isMultiPlot);
  const resolveRValue = getAxisValueResolver(getPolarAxis(input.data, 'r', input.layout)._type);

  input.data.forEach((series: Partial<PlotData>, index: number) => {
    const legend = legends[index];

    if (series.type === 'scatterpolar') {
      const isAreaTrace = series.fill === 'toself' || series.fill === 'tonext';
      const isLineTrace = typeof series.mode === 'undefined' ? true : series.mode.includes('lines');
      const colors = isAreaTrace ? series.fillcolor : isLineTrace ? series.line?.color : series.marker?.color;
      const extractedColors = extractColor(
        input.layout?.template?.layout?.colorway,
        colorwayType,
        colors,
        colorMap,
        isDarkTheme,
      );
      const seriesColor = resolveColor(
        extractedColors,
        index,
        legend,
        colorMap,
        input.layout?.template?.layout?.colorway,
        isDarkTheme,
      );
      const seriesOpacity = getOpacity(series, index);
      const finalSeriesColor = rgb(seriesColor).copy({ opacity: seriesOpacity }).formatHex8();
      const lineOptions = getLineOptions(series.line);
      const thetaUnit = (series as { thetaunit?: 'radians' | 'degrees' | 'gradians' }).thetaunit;

      const commonProps = {
        legend,
        legendShape: getLegendShape(series),
        color: finalSeriesColor,
        data:
          series.r
            ?.map((r, rIndex) => {
              const theta = series.theta?.[rIndex];
              const markerSize = Array.isArray(series.marker?.size) ? series.marker.size[rIndex] : series.marker?.size;
              const text = Array.isArray(series.text) ? series.text[rIndex] : series.text;
              const markerColor = resolveColor(
                extractedColors,
                rIndex,
                legend,
                colorMap,
                input.layout?.template?.layout?.colorway,
                isDarkTheme,
              );
              const markerOpacity = getOpacity(series, rIndex);

              if (isInvalidValue(resolveRValue(r)) || isInvalidValue(theta)) {
                return;
              }

              return {
                r: resolveRValue(r)!,
                theta:
                  typeof theta === 'number'
                    ? thetaUnit === 'radians'
                      ? (theta * 180) / Math.PI
                      : thetaUnit === 'gradians'
                      ? theta * 0.9
                      : theta
                    : (theta as string),
                color: markerColor ? rgb(markerColor).copy({ opacity: markerOpacity }).formatHex8() : finalSeriesColor,
                ...(typeof markerSize !== 'undefined' ? { markerSize } : {}),
                ...(typeof text !== 'undefined' ? { text } : {}),
              };
            })
            .filter(item => typeof item !== 'undefined') || [],
      };

      if (isAreaTrace || isLineTrace) {
        polarData.push({
          type: isAreaTrace ? 'areapolar' : 'linepolar',
          ...commonProps,
          lineOptions,
        });
      } else {
        polarData.push({
          type: 'scatterpolar',
          ...commonProps,
        });
      }
    }
  });

  return {
    data: polarData,
    width: input.layout?.width,
    height: input.layout?.height ?? 400,
    hideLegend,
    ...getPolarAxisProps(input.data, input.layout),
    //  ...getTitles(input.layout),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPlainObject(obj: any) {
  return (
    Object.prototype.toString.call(obj) === '[object Object]' &&
    Object.getPrototypeOf(obj).hasOwnProperty('hasOwnProperty')
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var arrayAttributes: any[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var stack: any[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isArrayStack: any[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var baseContainer: any, baseAttrName: any;
/**
 * Interate iteratively through the trace object and find all the array attributes.
 * 1 trace record = 1 series of data
 * @param trace
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findArrayAttributes(trace: any): void {
  // Init basecontainer and baseAttrName
  crawlIntoTrace(baseContainer, 0, '');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function crawlIntoTrace(container: any, i: number, astrPartial: any) {
  var item = container[stack[i]];
  var newAstrPartial = astrPartial + stack[i];
  if (i === stack.length - 1) {
    if (isArrayOrTypedArray(item)) {
      arrayAttributes.push(baseAttrName + newAstrPartial);
    }
  } else {
    if (isArrayStack[i]) {
      if (Array.isArray(item)) {
        for (var j = 0; j < item.length; j++) {
          if (isPlainObject(item[j])) {
            crawlIntoTrace(item[j], i + 1, newAstrPartial + '[' + j + '].');
          }
        }
      }
    } else if (isPlainObject(item)) {
      crawlIntoTrace(item, i + 1, newAstrPartial + '.');
    }
  }
}

function getLineOptions(line: Partial<ScatterLine> | undefined): LineChartLineOptions | undefined {
  if (!line) {
    return;
  }

  let lineOptions: LineChartLineOptions = {};
  if (line.dash) {
    lineOptions = { ...lineOptions, ...dashOptions[line.dash] };
  }

  switch (line.shape) {
    case 'spline':
      const smoothing = typeof line.smoothing === 'number' ? line.smoothing : 1;
      lineOptions.curve = d3CurveCardinal.tension(1 - smoothing / 1.3);
      break;
    case 'hv':
      lineOptions.curve = 'stepAfter';
      break;
    case 'vh':
      lineOptions.curve = 'stepBefore';
      break;
    case 'hvh':
      lineOptions.curve = 'step';
      break;
    default:
      lineOptions.curve = 'linear';
  }

  return Object.keys(lineOptions).length > 0 ? lineOptions : undefined;
}

// TODO: Use binary search to find the appropriate bin for numeric value.
const findBinIndex = (
  bins: string[][] | Bin<number, number>[],
  value: string | number | null | undefined,
  isString: boolean,
) => {
  if (typeof value === 'undefined' || value === null) {
    return -1;
  }

  return isString
    ? (bins as string[][]).findIndex(bin => bin.includes(value as string))
    : (bins as Bin<number, number>[]).findIndex(
        (bin, index) =>
          (value as number) >= bin.x0! &&
          (index === bins.length - 1 ? (value as number) <= bin.x1! : (value as number) < bin.x1!),
      );
};

const getBinSize = (bin: Bin<number, number>) => {
  return bin.x1! - bin.x0!;
};

const getBinCenter = (bin: Bin<number, number>) => {
  return (bin.x1! + bin.x0!) / 2;
};

// TODO: Add support for date axes
const createBins = (
  data: TypedArray | Datum[] | Datum[][] | undefined,
  binStart?: number | string,
  binEnd?: number | string,
  binSize?: number | string,
) => {
  if (!data || data.length === 0) {
    return [];
  }

  if (isStringArray(data)) {
    const categories = Array.from(new Set(data as string[]));
    const start = typeof binStart === 'number' ? Math.ceil(binStart) : 0;
    const stop = typeof binEnd === 'number' ? Math.floor(binEnd) + 1 : categories.length;
    const step = typeof binSize === 'number' ? binSize : 1;

    return d3Range(start, stop, step).map(i => categories.slice(i, i + step));
  }

  const scale = d3ScaleLinear()
    .domain(d3Extent<number>(data as number[]) as [number, number])
    .nice();
  let [minVal, maxVal] = scale.domain();

  minVal = typeof binStart === 'number' ? binStart : minVal;
  maxVal = typeof binEnd === 'number' ? binEnd : maxVal;

  const binGenerator = d3Bin().domain([minVal, maxVal]);

  if (typeof binSize === 'number' && binSize > 0) {
    const thresholds: number[] = [];
    const precision = Math.max(calculatePrecision(minVal), calculatePrecision(binSize));
    let th = precisionRound(minVal, precision);

    while (th < precisionRound(maxVal + binSize, precision)) {
      thresholds.push(th);
      th = precisionRound(th + binSize, precision);
    }

    minVal = thresholds[0];
    maxVal = thresholds[thresholds.length - 1];
    binGenerator.domain([minVal, maxVal]).thresholds(thresholds);

    // When the domain ends at the last threshold (maxVal), d3Bin creates an extra final bin where
    // both x0 and x1 are equal to maxVal and inclusive. The previous bin also has x1 equal to maxVal,
    // but it is exclusive. To maintain consistent bin widths, remove the final bin,
    // making the previous bin the last one, with both x0 and x1 inclusive.
    return binGenerator(data as number[]).slice(0, -1);
  }
  return binGenerator(data as number[]);
};

const calculateHistFunc = (histfunc: PlotData['histfunc'] | undefined, bin: number[]) => {
  switch (histfunc) {
    case 'sum':
      return d3Sum(bin);
    case 'avg':
      return bin.length === 0 ? 0 : d3Sum(bin) / bin.length;
    case 'min':
      return d3Min(bin) ?? 0;
    case 'max':
      return d3Max(bin) ?? 0;
    default:
      return bin.length;
  }
};

const calculateHistNorm = (
  histnorm: PlotData['histnorm'] | undefined,
  value: number,
  total: number,
  dx: number,
  dy: number = 1,
) => {
  switch (histnorm) {
    case 'percent':
      return total === 0 ? 0 : (value / total) * 100;
    case 'probability':
      return total === 0 ? 0 : value / total;
    case 'density':
      return dx * dy === 0 ? 0 : value / (dx * dy);
    case 'probability density':
      return total * dx * dy === 0 ? 0 : value / (total * dx * dy);
    default:
      return value;
  }
};

const getLegendShape = (series: Partial<PlotData>): Legend['shape'] => {
  const dashType = series.line?.dash || 'solid';
  if (dashType === 'dot' || dashType === 'dash' || dashType === 'dashdot') {
    return 'dottedLine';
  } else if (series.mode?.includes('markers')) {
    return 'circle';
  }
  return 'default';
};

export const getAllupLegendsProps = (
  input: PlotlySchema,
  colorMap: React.RefObject<Map<string, string>>,
  colorwayType: ColorwayType,
  traceInfo: TraceInfo[],
  isDarkTheme?: boolean,
): LegendsProps => {
  const allupLegends: Legend[] = [];
  // reduce on showlegend boolean propperty. reduce should return true if at least one series has showlegend true
  const toShowLegend = input.data.reduce((acc, series) => {
    return (
      acc || (series as Partial<PlotData>).showlegend === true || (series as Partial<PlotData>).showlegend === undefined
    );
  }, false);

  if (toShowLegend) {
    input.data.forEach((series: Data, index) => {
      if (traceInfo[index].type === 'donut') {
        const pieSeries = series as Partial<PieData>;
        const colors: string[] | string | null | undefined = extractColor(
          input.layout?.piecolorway ?? input.layout?.template?.layout?.colorway,
          colorwayType,
          input.layout?.piecolorway ?? pieSeries?.marker?.colors,
          colorMap,
          isDarkTheme,
          true,
        );

        if (isArrayOrTypedArray(pieSeries.labels)) {
          pieSeries.labels!.forEach((label, labelIndex: number) => {
            const legend = `${label}`;
            // resolve color for each legend from the extracted colors
            const color: string = resolveColor(
              colors,
              labelIndex,
              legend,
              colorMap,
              input.layout?.piecolorway ?? input.layout?.template?.layout?.colorway,
              isDarkTheme,
              true,
            );
            if (legend !== '' && allupLegends.some(group => group.title === legend) === false) {
              allupLegends.push({
                title: legend,
                color,
              });
            }
          });
        }
      } else if (isNonPlotType(traceInfo[index].type) === false) {
        const plotSeries = series as Partial<PlotData>;
        const name = plotSeries.legendgroup;
        const color = plotSeries.line?.color || plotSeries.marker?.color;
        const legendShape = getLegendShape(plotSeries);
        const resolvedColor = extractColor(
          input.layout?.template?.layout?.colorway,
          colorwayType,
          color,
          colorMap,
          isDarkTheme,
        );
        if (name !== undefined && name !== '' && allupLegends.some(group => group.title === name) === false) {
          allupLegends.push({
            title: name,
            color: resolvedColor as string,
            shape: legendShape,
          });
        }
      }
    });
  }

  return {
    legends: allupLegends,
    centerLegends: true,
    enabledWrapLines: true,
    canSelectMultipleLegends: true,
  };
};

const getLegendProps = (data: Data[], layout: Partial<Layout> | undefined, isMultiPlot: boolean) => {
  const legends: string[] = [];
  if (data.length === 1) {
    legends.push(data[0].name || '');
  } else {
    data.forEach((series, index) => {
      legends.push(series.name || `Series ${index + 1}`);
    });
  }

  const hideLegendsData = data.every((series: Partial<PlotData>) => series.showlegend === false);
  const hideLegendsInferred = layout?.showlegend === false || (layout?.showlegend !== true && legends.length < 2);

  return {
    legends,
    hideLegend: isMultiPlot || hideLegendsInferred || hideLegendsData,
  };
};

export const getNumberAtIndexOrDefault = (data: PlotData['z'] | undefined, index: number): number | undefined => {
  if (isArrayOrTypedArray(data)) {
    if (typeof data![index] !== 'number' || !isFinite(data![index] as number)) {
      return;
    }

    return data![index] as number;
  }

  return 1;
};

export const getValidXYRanges = (
  series: Partial<PlotData>,
  resolveX?: (v: Datum) => Datum,
  resolveY?: (v: Datum) => Datum,
): [number, number][] => {
  if (!isArrayOrTypedArray(series.x) || !isArrayOrTypedArray(series.y)) {
    return [];
  }

  const ranges: [number, number][] = [];
  let start = 0;
  let end = 0;
  for (; end < series.x!.length; end++) {
    if (
      isInvalidValue(series.x![end]) ||
      (typeof resolveX === 'function' && isInvalidValue(resolveX(series.x![end] as Datum))) ||
      isInvalidValue(series.y![end]) ||
      (typeof resolveY === 'function' && isInvalidValue(resolveY(series.y![end] as Datum)))
    ) {
      if (end - start > 0) {
        ranges.push([start, end]);
      }
      start = end + 1;
    }
  }
  if (end - start > 0) {
    ranges.push([start, end]);
  }

  return ranges;
};

const getIndexFromKey = (key: string, pattern: string): number => {
  const normalizedKey = key.replace(pattern, '') === '' ? '1' : key.replace(pattern, '');
  return parseInt(normalizedKey, 10) - 1;
};

export const isNonPlotType = (chartType: string): boolean => {
  return ['donut', 'sankey', 'pie', 'annotation', 'table', 'gauge', 'funnel'].includes(chartType);
};

export const getGridProperties = (
  schema: PlotlySchema | undefined,
  isMultiPlot: boolean,
  validTracesInfo: TraceInfo[],
): GridProperties => {
  const domainX: ExtDomainInterval[] = [];
  const domainY: ExtDomainInterval[] = [];
  let cartesianDomains = 0;
  type AnnotationProps = {
    xAnnotation?: string;
    yAnnotation?: string;
  };
  const annotations: Record<number, AnnotationProps> = {};
  let templateRows = '1fr';
  let templateColumns = '1fr';
  const gridLayout: GridAxisProperties = {};
  if (!isMultiPlot) {
    return { templateRows, templateColumns, layout: gridLayout };
  }

  const layout = schema?.layout as Partial<Layout> | undefined;

  if (layout !== undefined && layout !== null && Object.keys(layout).length > 0) {
    Object.keys(layout ?? {}).forEach(key => {
      if (key.startsWith('xaxis')) {
        const index = getIndexFromKey(key, 'xaxis');
        const anchor = (layout[key as keyof typeof layout] as Partial<LayoutAxis>)?.anchor ?? 'y';
        const anchorIndex = getIndexFromKey(anchor, 'y');
        if (index !== anchorIndex) {
          throw new Error(`Invalid layout: xaxis ${index + 1} anchor should be y${anchorIndex + 1}`);
        }
        const xAxisLayout = layout[key as keyof typeof layout] as Partial<LayoutAxis>;
        const domainXInfo: ExtDomainInterval = {
          start: xAxisLayout?.domain ? xAxisLayout.domain[0] : 0,
          end: xAxisLayout?.domain ? xAxisLayout.domain[1] : 1,
          cellName: `x${domainX.length === 0 ? '' : domainX.length + 1}` as XAxisName,
        };
        domainX.push(domainXInfo);
      } else if (key.startsWith('yaxis')) {
        const index = getIndexFromKey(key, 'yaxis');
        const anchor = (layout[key as keyof typeof layout] as Partial<LayoutAxis>)?.anchor ?? 'x';
        const anchorIndex = getIndexFromKey(anchor, 'x');
        if (index !== anchorIndex) {
          if ((index === 1 && anchorIndex === 0) || layout.yaxis2?.side === 'right') {
            // Special case for secondary y axis where yaxis2 can anchor to x1
            return { templateRows, templateColumns };
          }
          throw new Error(`Invalid layout: yaxis ${index + 1} anchor should be x${anchorIndex + 1}`);
        }
        const yAxisLayout = layout[key as keyof typeof layout] as Partial<LayoutAxis>;
        const domainYInfo: ExtDomainInterval = {
          start: yAxisLayout?.domain ? yAxisLayout.domain[0] : 0,
          end: yAxisLayout?.domain ? yAxisLayout.domain[1] : 1,
          cellName: `x${domainY.length === 0 ? '' : domainY.length + 1}` as XAxisName,
        };
        domainY.push(domainYInfo);
      }
    });
  }

  cartesianDomains = domainX.length; // Assuming that the number of x and y axes is the same
  validTracesInfo.forEach((trace, index) => {
    if (isNonPlotType(trace.type)) {
      const series = schema?.data?.[index] as Partial<PieData> | Partial<SankeyData>;
      const domainXInfo: ExtDomainInterval = {
        start: series.domain?.x ? series.domain.x[0] : 0,
        end: series.domain?.x ? series.domain.x[1] : 1,
        cellName: `${NON_PLOT_KEY_PREFIX}${domainX.length - cartesianDomains + 1}`,
      };
      const domainYInfo: ExtDomainInterval = {
        start: series.domain?.y ? series.domain.y[0] : 0,
        end: series.domain?.y ? series.domain.y[1] : 1,
        cellName: `${NON_PLOT_KEY_PREFIX}${domainY.length - cartesianDomains + 1}`,
      };
      domainX.push(domainXInfo);
      domainY.push(domainYInfo);
    }
  });

  if (layout !== undefined && layout !== null && Object.keys(layout).length > 0) {
    Object.keys(layout ?? {}).forEach(key => {
      if (key.startsWith('polar')) {
        const polarLayout = layout[key as keyof Layout] as Partial<PolarLayout>;
        const domainXInfo: ExtDomainInterval = {
          start: polarLayout.domain?.x ? polarLayout.domain.x[0] : 0,
          end: polarLayout.domain?.x ? polarLayout.domain.x[1] : 1,
          cellName: key,
        };
        const domainYInfo: ExtDomainInterval = {
          start: polarLayout.domain?.y ? polarLayout.domain.y[0] : 0,
          end: polarLayout.domain?.y ? polarLayout.domain.y[1] : 1,
          cellName: key,
        };
        domainX.push(domainXInfo);
        domainY.push(domainYInfo);
      }
    });
    layout.annotations?.forEach(annotation => {
      const xMatches = domainX.flatMap((interval, idx) =>
        (annotation?.x as number) >= interval.start && (annotation?.x as number) <= interval.end ? [idx] : [],
      );
      const yMatch = domainY.findIndex(
        (interval, yIndex) =>
          xMatches.includes(yIndex) &&
          (annotation?.y as number) >= interval.start &&
          (annotation?.y as number) <= interval.end,
      );

      if (yMatch !== -1) {
        if (annotations[yMatch] === undefined) {
          annotations[yMatch] = {} as AnnotationProps;
        }
        if ((annotation?.textangle as number) === 90) {
          annotations[yMatch].yAnnotation = annotation.text;
        } else {
          annotations[yMatch].xAnnotation = annotation.text;
        }
      }
    });
  }

  if (domainX.length > 0) {
    const uniqueXIntervals = new Map<string, ExtDomainInterval>();
    domainX.forEach(interval => {
      const key = `${interval.start}-${interval.end}`;
      if (!uniqueXIntervals.has(key)) {
        uniqueXIntervals.set(key, interval);
      }
    });
    const sortedXStart = Array.from(uniqueXIntervals.values())
      .map(interval => interval.start)
      .sort();

    templateColumns = `repeat(${sortedXStart.length}, 1fr)`;

    domainX.forEach((interval, index) => {
      const columnIndex = sortedXStart.findIndex(start => start === interval.start);
      const columnNumber = columnIndex + 1; // Column numbers are 1-based

      const annotationProps = annotations[index] as AnnotationProps;
      const xAnnotation = annotationProps?.xAnnotation;

      const row: AxisProperties = {
        row: -1,
        column: columnNumber,
        xAnnotation,
        xDomain: interval,
        yDomain: { start: 0, end: 1 }, // Default yDomain for x-axis
      };
      gridLayout[interval.cellName] = row;
    });
  }
  if (domainY.length > 0) {
    const uniqueYIntervals = new Map<string, ExtDomainInterval>();
    domainY.forEach(interval => {
      const key = `${interval.start}-${interval.end}`;
      if (!uniqueYIntervals.has(key)) {
        uniqueYIntervals.set(key, interval);
      }
    });
    const sortedYStart = Array.from(uniqueYIntervals.values())
      .map(interval => interval.start)
      .sort();

    const numberOfRows = sortedYStart.length;

    templateRows = `repeat(${numberOfRows}, 1fr)`;
    domainY.forEach((interval, index) => {
      const rowIndex = sortedYStart.findIndex(start => start === interval.start);
      const rowNumber = numberOfRows - rowIndex; // Rows are 1-based and we need to reverse the order for CSS grid

      const annotationProps = annotations[index] as AnnotationProps;
      const yAnnotation = annotationProps?.yAnnotation;

      const cell = gridLayout[interval.cellName];

      if (cell !== undefined) {
        cell.row = rowNumber;
        cell.yAnnotation = yAnnotation;
        cell.yDomain = interval;
      }
    });
  }

  return {
    templateRows,
    templateColumns,
    layout: gridLayout,
  };
};

type GetAxisCategoryOrderPropsResult = Pick<CartesianChartProps, 'xAxisCategoryOrder' | 'yAxisCategoryOrder'>;

/**
 * @see {@link https://github.com/plotly/plotly.js/blob/master/src/plots/cartesian/category_order_defaults.js#L50}
 */
const getAxisCategoryOrderProps = (data: Data[], layout: Partial<Layout> | undefined) => {
  const result: GetAxisCategoryOrderPropsResult = {};

  const axesById: Record<string, Partial<LayoutAxis> | undefined> = {
    x: layout?.xaxis,
    y: layout?.yaxis,
  };
  Object.keys(axesById).forEach(axId => {
    const ax = axesById[axId];
    const axLetter = axId[0] as 'x' | 'y';
    const propName = `${axLetter}AxisCategoryOrder` as keyof GetAxisCategoryOrderPropsResult;

    const values: Datum[] = [];
    data.forEach((series: Partial<PlotData>) => {
      series[axLetter]?.forEach(val => {
        if (!isInvalidValue(val)) {
          values.push(val as Datum);
        }
      });
    });

    const isAxisTypeCategory =
      ax?.type === 'category' || (isStringArray(values) && !isNumberArray(values) && !isDateArray(values));
    if (!isAxisTypeCategory) {
      result[propName] = 'data';
      return;
    }

    const isValidArray = isArrayOrTypedArray(ax?.categoryarray) && ax!.categoryarray!.length > 0;
    if (isValidArray && (!ax?.categoryorder || ax.categoryorder === 'array')) {
      result[propName] = ax?.autorange === 'reversed' ? ax!.categoryarray!.slice().reverse() : ax!.categoryarray;
      return;
    }

    if (!ax?.categoryorder || ax.categoryorder === 'trace' || ax.categoryorder === 'array') {
      const categoriesInTraceOrder = Array.from(new Set(values as string[]));
      result[propName] = ax?.autorange === 'reversed' ? categoriesInTraceOrder.reverse() : categoriesInTraceOrder;
      return;
    }

    result[propName] = ax.categoryorder;
  });

  return result;
};

const getBarProps = (
  data: Data[],
  layout: Partial<Layout> | undefined,
  isHorizontal?: boolean,
):
  | Pick<VerticalBarChartProps, 'barWidth' | 'maxBarWidth' | 'xAxisInnerPadding' | 'xAxisOuterPadding' | 'xAxisPadding'>
  | Pick<GanttChartProps, 'barHeight' | 'maxBarHeight' | 'yAxisPadding'> => {
  let padding: number | undefined;

  if (typeof layout?.bargap === 'number') {
    if (layout.bargap >= 0 && layout.bargap <= 1) {
      padding = layout.bargap;
    } else {
      // Plotly uses a default bargap of 0.2, as noted here: https://github.com/plotly/plotly.js/blob/1d5a249e43dd31ae50acf02117a19e5ac97387e9/src/traces/bar/layout_defaults.js#L58.
      // However, we don't use this value as our default padding because it causes the bars to
      // appear disproportionately wide in large containers.
      padding = 0.2;
    }
  }

  const plotlyBarWidths = data
    .map((series: Partial<PlotData>) => {
      if (series.type === 'bar' && (isArrayOrTypedArray(series.width) || typeof series.width === 'number')) {
        return series.width;
      }
      return [];
    })
    .flat();
  const maxPlotlyBarWidth = d3Max(plotlyBarWidths as number[]);
  if (typeof maxPlotlyBarWidth === 'number') {
    padding = 1 - maxPlotlyBarWidth;
    padding = Math.max(0, Math.min(padding, 1));
  }

  if (typeof padding === 'undefined') {
    return {};
  }

  if (isHorizontal) {
    return {
      maxBarHeight: 1000,
      yAxisPadding: padding,
    };
  }

  return {
    barWidth: 'auto',
    maxBarWidth: 1000,
    xAxisInnerPadding: padding,
    xAxisOuterPadding: padding / 2,
  };
};

type GetAxisScaleTypePropsResult = Pick<CartesianChartProps, 'xScaleType' | 'yScaleType' | 'secondaryYScaleType'>;

const getAxisScaleTypeProps = (data: Data[], layout: Partial<Layout> | undefined): GetAxisScaleTypePropsResult => {
  const result: GetAxisScaleTypePropsResult = {};

  const axisObjects = getAxisObjects(data, layout);

  if (axisObjects.x?.type === 'log') {
    result.xScaleType = 'log';
  }
  if (axisObjects.y?.type === 'log') {
    result.yScaleType = 'log';
  }
  if (axisObjects.y2?.type === 'log') {
    result.secondaryYScaleType = 'log';
  }

  return result;
};

type GetAxisTickPropsResult = Pick<
  CartesianChartProps,
  'tickValues' | 'xAxisTickCount' | 'xAxis' | 'yAxisTickValues' | 'yAxisTickCount' | 'yAxis'
>;

/**
 * @see {@link https://github.com/plotly/plotly.js/blob/master/src/plots/cartesian/tick_value_defaults.js#L8}
 */
const getAxisTickProps = (data: Data[], layout: Partial<Layout> | undefined): GetAxisTickPropsResult => {
  const props: GetAxisTickPropsResult = {};
  const axisObjects = getAxisObjects(data, layout);

  Object.keys(axisObjects).forEach(axId => {
    const ax = axisObjects[axId];
    if (!ax) {
      return;
    }

    const axType = getAxisType(data, ax);

    if (axId === 'x' && axType === 'category') {
      props.xAxis = {
        tickLayout: 'auto',
      };
    }

    if ((!ax.tickmode || ax.tickmode === 'array') && isArrayOrTypedArray(ax.tickvals)) {
      const tickValues = axType === 'date' ? ax.tickvals!.map((v: string | number | Date) => new Date(v)) : ax.tickvals;

      if (axId === 'x') {
        props.tickValues = tickValues;
        props.xAxis = {
          ...props.xAxis,
          tickText: ax.ticktext,
        };
      } else if (axId === 'y') {
        props.yAxisTickValues = tickValues;
        props.yAxis = {
          ...props.yAxis,
          tickText: ax.ticktext,
        };
      }
      return;
    }

    if ((!ax.tickmode || ax.tickmode === 'linear') && ax.dtick) {
      const dtick = plotlyDtick(ax.dtick, axType);
      const tick0 = plotlyTick0(ax.tick0, axType, dtick);

      if (axId === 'x') {
        props.xAxis = {
          ...props.xAxis,
          tickStep: dtick,
          tick0,
        };
      } else if (axId === 'y') {
        props.yAxis = {
          ...props.yAxis,
          tickStep: dtick,
          tick0,
        };
      }
      return;
    }

    if ((!ax.tickmode || ax.tickmode === 'auto') && typeof ax.nticks === 'number' && ax.nticks >= 0) {
      if (axId === 'x') {
        props.xAxisTickCount = ax.nticks;
      } else if (axId === 'y') {
        props.yAxisTickCount = ax.nticks;
      }
    }
  });

  return props;
};

/**
 * @see {@link https://github.com/plotly/plotly.js/blob/master/src/plots/cartesian/clean_ticks.js#L16}
 */
const plotlyDtick = (dtick: DTickValue | undefined, axType: AxisType | undefined) => {
  const isLogAx = axType === 'log';
  const isDateAx = axType === 'date';
  const isCatAx = axType === 'category';
  const dtickDflt = isDateAx ? 86400000 : 1;

  if (!dtick) {
    return dtickDflt;
  }

  if (isNumber(dtick)) {
    dtick = Number(dtick);
    if (dtick <= 0) {
      return dtickDflt;
    }
    if (isCatAx) {
      // category dtick must be positive integers
      return Math.max(1, Math.round(dtick));
    }
    if (isDateAx) {
      // date dtick must be at least 0.1ms (our current precision)
      return Math.max(0.1, dtick);
    }
    return dtick;
  }

  if (typeof dtick !== 'string' || !(isDateAx || isLogAx)) {
    return dtickDflt;
  }

  const prefix = dtick.charAt(0);
  const dtickNum = isNumber(dtick.slice(1)) ? Number(dtick.slice(1)) : 0;

  if (
    dtickNum <= 0 ||
    !(
      // "M<n>" gives ticks every (integer) n months
      (
        (isDateAx && prefix === 'M' && dtickNum === Math.round(dtickNum)) ||
        // "L<f>" gives ticks linearly spaced in data (not in position) every (float) f
        (isLogAx && prefix === 'L') ||
        // "D1" gives powers of 10 with all small digits between, "D2" gives only 2 and 5
        (isLogAx && prefix === 'D' && (dtickNum === 1 || dtickNum === 2))
      )
    )
  ) {
    return dtickDflt;
  }

  return dtick;
};

/**
 * @see {@link https://github.com/plotly/plotly.js/blob/master/src/plots/cartesian/clean_ticks.js#L70}
 */
const plotlyTick0 = (tick0: number | string | undefined, axType: AxisType | undefined, dtick: string | number) => {
  if (axType === 'date') {
    return isDate(tick0) ? new Date(tick0!) : new Date(DEFAULT_DATE_STRING);
  }
  if (dtick === 'D1' || dtick === 'D2') {
    // D1 and D2 modes ignore tick0 entirely
    return undefined;
  }
  // Aside from date axes, tick0 must be numeric
  return isNumber(tick0) ? Number(tick0) : 0;
};

interface AxisObject extends Partial<LayoutAxis> {
  _id: string;
}

const getAxisObjects = (data: Data[], layout: Partial<Layout> | undefined) => {
  // Traces are grouped by their xaxis property, and for each group/subplot, the adapter functions
  // are called with the corresponding filtered data. As a result, all traces passed to an adapter
  // function share the same xaxis.
  let xAxisId: number | undefined;
  const yAxisIds = new Set<number>();
  data.forEach((series: Partial<PlotData>) => {
    const axisIds = getAxisIds(series);
    xAxisId = axisIds.x;
    yAxisIds.add(axisIds.y);
  });

  const makeAxisObject = (axLetter: 'x' | 'y', axId: number): AxisObject => ({
    ...layout?.[getAxisKey(axLetter, axId)],
    _id: `${axLetter}${axId > 1 ? axId : ''}`,
  });

  const axisObjects: Record<string, AxisObject> = {};

  if (typeof xAxisId === 'number') {
    axisObjects.x = makeAxisObject('x', xAxisId);
  }

  const sortedYAxisIds = Array.from(yAxisIds).sort();
  if (sortedYAxisIds.length > 0) {
    axisObjects.y = makeAxisObject('y', sortedYAxisIds[0]);
  }
  if (sortedYAxisIds.length > 1) {
    axisObjects.y2 = makeAxisObject('y', sortedYAxisIds[1]);
  }

  return axisObjects;
};

const getAxisType = (data: Data[], ax: AxisObject): AxisType => {
  if (!ax) {
    return 'category';
  }

  if (['linear', 'log', 'date', 'category'].includes(ax.type ?? '')) {
    return ax.type!;
  }

  const axLetter = ax._id[0] as 'x' | 'y';
  const values: Datum[] = [];
  data.forEach((series: Partial<PlotData>) => {
    const axId = series[`${axLetter}axis`];
    if ((axId === ax._id || (!axId && ax._id === axLetter)) && isArrayOrTypedArray(series[axLetter])) {
      series[axLetter]!.forEach(val => {
        if (!isInvalidValue(val)) {
          values.push(val as Datum);
        }
      });
    }
  });

  if (isNumberArray(values) && !isYearArray(values)) {
    return 'linear';
  }
  if (isDateArray(values)) {
    return 'date';
  }
  return 'category';
};

const getAxisValueResolver = (axType: AxisType, dateParser?: (v: string | number) => Date) => {
  const toDate = (value: string | number): Date | null => {
    const date = dateParser ? dateParser(value) : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  return (value: Datum): Datum => {
    if (isInvalidValue(value)) {
      return null;
    }

    switch (axType) {
      case 'linear':
      case 'log':
        return isNumber(value) ? +value! : null;

      case 'date':
        if (isNumber(value)) {
          return toDate(+value!);
        }
        if (typeof value === 'string') {
          return toDate(value);
        }
        return null;

      case 'category':
        return `${value}`;

      default:
        return null;
    }
  };
};

/**
 * This is experimental. Use it only with valid datetime strings to verify if they conform to the ISO 8601 format.
 */
const isoDateRegex = /^\d{4}(-\d{2}(-\d{2})?)?(T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z)?)?$/;

/**
 * We want to display localized date and time in the charts, so the useUTC prop is set to false.
 * But this can sometimes cause the formatters to display the datetime incorrectly.
 * To work around this issue, we use this function to adjust datetime strings so that they are always interpreted
 * as local time, allowing the formatters to produce the correct output.
 *
 * FIXME: The formatters should always produce a clear and accurate localized output, regardless of the
 * format used to create the date object.
 */
const parseLocalDate = (value: string | number) => {
  if (typeof value === 'string') {
    const match = value.match(isoDateRegex);
    if (match) {
      if (!match[3]) {
        value += 'T00:00';
      } else if (match[6]) {
        value = value.replace('Z', '');
      }
    }
  }
  return new Date(value);
};

type PolarDataKey = 'r' | 'theta';
interface PolarAxisObject extends Partial<LayoutAxis> {
  _type: AxisType;
  _dataKey: PolarDataKey;
}

const POLAR_AXIS_BY_DATA_KEY: Record<PolarDataKey, 'radialAxis' | 'angularAxis'> = {
  r: 'radialAxis',
  theta: 'angularAxis',
};
export const DEFAULT_POLAR_SUBPLOT = 'polar';

const getPolarLayout = (
  trace: Partial<PlotData>,
  layout: Partial<Layout> | undefined,
): Partial<PolarLayout> | undefined => {
  const subplotId = ((trace as { subplot?: string })?.subplot || DEFAULT_POLAR_SUBPLOT) as keyof Layout;
  return layout?.[subplotId];
};

const getValidAxisValues = (data: Data[], dataKey: PolarDataKey): Datum[] => {
  const values: Datum[] = [];
  data.forEach((series: Partial<PlotData>) => {
    if (isArrayOrTypedArray(series[dataKey])) {
      (series[dataKey] as Datum[]).forEach(val => {
        if (!isInvalidValue(val)) {
          values.push(val as Datum);
        }
      });
    }
  });
  return values;
};

const getPolarAxisType = (data: Data[], dataKey: PolarDataKey, declaredType: AxisType | undefined): AxisType => {
  if (['linear', 'log', 'date', 'category'].includes(declaredType ?? '')) {
    return declaredType!;
  }

  const values = getValidAxisValues(data, dataKey);
  if (isNumberArray(values) && !isYearArray(values)) {
    return 'linear';
  }
  if (isDateArray(values)) {
    return 'date';
  }
  return 'category';
};

const getPolarAxis = (data: Data[], dataKey: PolarDataKey, layout: Partial<Layout> | undefined): PolarAxisObject => {
  const polarLayout = getPolarLayout(data[0] as Partial<PlotData>, layout);
  const ax = polarLayout?.[POLAR_AXIS_BY_DATA_KEY[dataKey].toLowerCase() as 'radialaxis' | 'angularaxis'];
  return {
    ...ax,
    _dataKey: dataKey,
    _type: getPolarAxisType(data, dataKey, ax?.type),
  };
};

const getPolarAxisTickProps = (data: Data[], ax: PolarAxisObject): PolarAxisProps => {
  const props: PolarAxisProps = {};

  if ((!ax.tickmode || ax.tickmode === 'array') && isArrayOrTypedArray(ax.tickvals)) {
    const tickValues = ax._type === 'date' ? ax.tickvals!.map((v: string | number | Date) => new Date(v)) : ax.tickvals;

    props.tickValues = tickValues;
    props.tickText = ax.ticktext;
    return props;
  }

  if ((!ax.tickmode || ax.tickmode === 'linear') && ax.dtick) {
    const dtick = plotlyDtick(ax.dtick, ax._type);
    const tick0 = plotlyTick0(ax.tick0, ax._type, dtick);

    props.tickStep = dtick;
    props.tick0 = tick0;
    return props;
  }

  if ((!ax.tickmode || ax.tickmode === 'auto') && typeof ax.nticks === 'number' && ax.nticks >= 0) {
    props.tickCount = ax.nticks;
  }

  return props;
};

const getPolarAxisCategoryOrder = (data: Data[], ax: PolarAxisObject) => {
  if (ax._type !== 'category') {
    return 'data';
  }

  const isValidArray = isArrayOrTypedArray(ax.categoryarray) && ax.categoryarray!.length > 0;
  if (isValidArray && (!ax.categoryorder || ax.categoryorder === 'array')) {
    return ax.categoryarray;
  }

  if (!ax.categoryorder || ax.categoryorder === 'trace' || ax.categoryorder === 'array') {
    const values = getValidAxisValues(data, ax._dataKey);
    const categoriesInTraceOrder = Array.from(new Set(values as string[]));
    return ax.autorange === 'reversed' ? categoriesInTraceOrder.reverse() : categoriesInTraceOrder;
  }

  return ax.categoryorder;
};

const getPolarAxisProps = (data: Data[], layout: Partial<Layout> | undefined) => {
  const props: Partial<PolarChartProps> = {};

  (Object.keys(POLAR_AXIS_BY_DATA_KEY) as PolarDataKey[]).forEach(dataKey => {
    const propName = POLAR_AXIS_BY_DATA_KEY[dataKey];
    const ax = getPolarAxis(data, dataKey, layout);

    props[propName] = {
      scaleType: ax._type === 'log' ? 'log' : 'default',
      categoryOrder: getPolarAxisCategoryOrder(data, ax),
      tickFormat: ax.tickformat,
      ...getPolarAxisTickProps(data, ax),
      ...(isArrayOrTypedArray(ax.range) ? { rangeStart: ax.range![0], rangeEnd: ax.range![1] } : {}),
    };

    if (propName === 'angularAxis') {
      props[propName].unit = (ax as { thetaunit?: 'radians' | 'degrees' }).thetaunit;
      props.direction = ax.direction;
    }
  });

  return props;
};
